import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

// ── Supabase client ──────────────────────────────────────────────────────────
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

// ── Self-contained single file version ──
// All components, styles and data live here.
// No external imports needed except React.

const CITIES = ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Gold Coast", "Canberra", "Newcastle", "Wollongong", "Hobart", "Darwin", "Cairns", "Townsville", "Geelong", "Sunshine Coast", "Remote / Online"];
const SKILLS = [
  "HTML / CSS", "JavaScript", "TypeScript", "React",
  "Node.js", "Python", "Django", "Ruby on Rails", "PHP", "Laravel", "Java", "C#", ".NET", "C / C++",
  "React Native", "Android", "iOS",
  "PostgreSQL", "MySQL", "AWS", "Google Cloud", "Azure", "DevOps / CI-CD", "REST APIs",
  "UI/UX Design", "Shopify", "WordPress", "Data Science", "Machine Learning / AI", "Cybersecurity", "SEO / Analytics",
];

const SKILL_GROUPS = [
  { label: "Frontend", skills: ["HTML / CSS", "JavaScript", "TypeScript", "React"] },
  { label: "Backend", skills: ["Node.js", "Python", "Django", "Ruby on Rails", "PHP", "Laravel", "Java", "C#", ".NET", "C / C++"] },
  { label: "Mobile", skills: ["React Native", "Android", "iOS"] },
  { label: "Database & Cloud", skills: ["PostgreSQL", "MySQL", "AWS", "Google Cloud", "Azure", "DevOps / CI-CD", "REST APIs"] },
  { label: "Design", skills: ["UI/UX Design"] },
  { label: "Platform & CMS", skills: ["Shopify", "WordPress"] },
  { label: "Data & Other", skills: ["Data Science", "Machine Learning / AI", "Cybersecurity", "SEO / Analytics"] },
];

const MOCK_DEVS = [];
const MOCK_PROJECTS = [];

const INDUSTRIES = ["Health & Wellness", "Fintech", "EdTech", "E-commerce", "Social Impact", "Fashion & Beauty", "Food & Hospitality", "Media & Content", "Real Estate", "Legal Tech"];
const URGENCY = ["Just exploring", "Within 3 months", "Within 6 weeks", "ASAP - ready now"];
const initials = name => name.split(" ").map(n => n[0]).join("");



// ── STYLES ────────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --cream: #FAF7F2; --black: #0D0D0D; --rose: #E8355A; --gold: #C9A84C;
    --mid: #3D3D3D; --muted: #8A8A8A; --border: #E8E4DE;
    --card-shadow: 0 4px 24px rgba(0,0,0,0.07);
  }
  html { scroll-behavior: smooth; }
  body { font-family: 'DM Sans', sans-serif; background: var(--cream); color: var(--black); min-height: 100vh; }
  .noise { position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.025'/%3E%3C/svg%3E"); }
  .app { position: relative; z-index: 1; padding-bottom: 80px; }

  /* NAV */
  .nav { display:flex; align-items:center; justify-content:space-between; padding:1.1rem 2.5rem; border-bottom:1px solid var(--border); background:rgba(250,247,242,0.92); backdrop-filter:blur(14px); position:sticky; top:0; z-index:100; }
  .nav-logo { font-family:'Playfair Display',serif; font-size:1.35rem; font-weight:900; letter-spacing:-0.02em; cursor:pointer; }
  .nav-logo span { color:var(--rose); font-style:italic; }
  .nav-tabs { display:flex; gap:0.2rem; background:var(--black); border-radius:100px; padding:0.28rem; }
  .nav-tab { padding:0.45rem 1.05rem; border-radius:100px; border:none; font-family:'DM Sans',sans-serif; font-size:0.8rem; font-weight:500; cursor:pointer; transition:all 0.2s; background:transparent; color:#777; white-space:nowrap; }
  .nav-tab.active { background:var(--rose); color:white; }
  .nav-right { display:flex; gap:0.75rem; align-items:center; }
  .saved-btn { background:transparent; border:1.5px solid var(--border); padding:0.5rem 1rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.8rem; font-weight:600; cursor:pointer; transition:all 0.2s; color:var(--mid); display:flex; align-items:center; gap:0.4rem; }
  .saved-btn:hover { border-color:var(--rose); color:var(--rose); }
  .saved-count { background:var(--rose); color:white; border-radius:100px; padding:0.1rem 0.45rem; font-size:0.66rem; font-weight:700; }
  .nav-cta { background:var(--black); color:white; border:none; padding:0.55rem 1.3rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.8rem; font-weight:600; cursor:pointer; transition:all 0.2s; }
  .nav-cta:hover { background:var(--rose); }

  /* MOBILE NAV SVG ICONS */
  .mobile-nav-icon { width:22px; height:22px; display:flex; align-items:center; justify-content:center; }
  .mobile-nav-icon svg { width:20px; height:20px; stroke:currentColor; fill:none; stroke-width:1.75; stroke-linecap:round; stroke-linejoin:round; }
  .mobile-nav { display:none; position:fixed; bottom:0; left:0; right:0; z-index:100; background:rgba(13,13,13,0.97); backdrop-filter:blur(16px); padding:0.7rem 1rem calc(0.7rem + env(safe-area-inset-bottom)); border-top:1px solid #222; }
  .mobile-nav-inner { display:flex; justify-content:space-around; }
  .mobile-nav-btn { display:flex; flex-direction:column; align-items:center; gap:0.28rem; background:none; border:none; cursor:pointer; color:#555; transition:all 0.2s; padding:0.25rem 0.5rem; border-radius:12px; }
  .mobile-nav-btn.active { color:var(--rose); }
  .mobile-nav-icon { font-size:1.25rem; }
  .mobile-nav-label { font-family:'DM Sans',sans-serif; font-size:0.6rem; font-weight:600; letter-spacing:0.04em; text-transform:uppercase; }

  /* MOBILE NAV */
  .mobile-nav { display:none; position:fixed; bottom:0; left:0; right:0; z-index:100; background:rgba(13,13,13,0.97); backdrop-filter:blur(16px); padding:0.7rem 1rem calc(0.7rem + env(safe-area-inset-bottom)); border-top:1px solid #222; }
  .mobile-nav-inner { display:flex; justify-content:space-around; }
  .mobile-nav-btn { display:flex; flex-direction:column; align-items:center; gap:0.28rem; background:none; border:none; cursor:pointer; color:#555; transition:all 0.2s; padding:0.25rem 0.5rem; border-radius:12px; }
  .mobile-nav-btn.active { color:var(--rose); }
  .mobile-nav-label { font-family:'DM Sans',sans-serif; font-size:0.6rem; font-weight:600; letter-spacing:0.04em; text-transform:uppercase; }
  /* SEARCH */
  .search-bar-wrap { max-width:1200px; margin:0 auto; padding:1.5rem 2.5rem 0; }
  .search-bar { display:flex; align-items:center; gap:1rem; background:white; border:1.5px solid var(--border); border-radius:100px; padding:0.7rem 1.25rem; transition:all 0.2s; box-shadow:var(--card-shadow); }
  .search-bar:focus-within { border-color:var(--rose); box-shadow:0 0 0 3px rgba(232,53,90,0.08); }
  .search-icon { width:18px; height:18px; flex-shrink:0; color:var(--muted); }
  .search-icon svg { width:18px; height:18px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; }
  .search-input { flex:1; border:none; outline:none; font-family:'DM Sans',sans-serif; font-size:0.9rem; background:transparent; color:var(--black); }
  .search-input::placeholder { color:var(--muted); }
  .search-clear { background:var(--cream); border:none; width:26px; height:26px; border-radius:50%; cursor:pointer; font-size:0.75rem; color:var(--muted); display:flex; align-items:center; justify-content:center; transition:all 0.2s; flex-shrink:0; }
  .search-clear:hover { background:var(--black); color:white; }

  /* HERO */
  .hero { display:grid; grid-template-columns:1fr 1fr; gap:4rem; padding:4.5rem 2.5rem 3.5rem; max-width:1200px; margin:0 auto; align-items:center; }
  .hero-eyebrow { display:inline-flex; align-items:center; gap:0.5rem; background:var(--black); color:var(--gold); font-size:0.7rem; font-weight:700; letter-spacing:0.13em; text-transform:uppercase; padding:0.38rem 0.9rem; border-radius:100px; margin-bottom:1.5rem; }
  .hero-eyebrow::before { content:''; width:6px; height:6px; background:var(--gold); border-radius:50%; animation:pulse 2s infinite; }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
  .hero-title { font-family:'Playfair Display',serif; font-size:clamp(2.6rem,4.5vw,4rem); font-weight:900; line-height:1.06; letter-spacing:-0.03em; margin-bottom:1.4rem; }
  .hero-title em { font-style:italic; color:var(--rose); }
  .hero-sub { font-size:1rem; color:var(--mid); line-height:1.75; margin-bottom:2.25rem; font-weight:300; max-width:460px; }
  .hero-actions { display:flex; gap:0.9rem; flex-wrap:wrap; }
  .btn-primary { background:var(--rose); color:white; border:none; padding:0.85rem 1.9rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.92rem; font-weight:600; cursor:pointer; transition:all 0.25s; }
  .btn-primary:hover { background:#c42047; transform:translateY(-2px); box-shadow:0 8px 24px rgba(232,53,90,0.28); }
  .btn-secondary { background:transparent; color:var(--black); border:1.5px solid var(--black); padding:0.85rem 1.9rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.92rem; font-weight:600; cursor:pointer; transition:all 0.25s; }
  .btn-secondary:hover { background:var(--black); color:white; transform:translateY(-2px); }
  .hero-stats { display:flex; gap:2rem; margin-top:2.5rem; padding-top:2rem; border-top:1px solid var(--border); }
  .hero-stat-num { font-family:'Playfair Display',serif; font-size:1.75rem; font-weight:900; }
  .hero-stat-label { font-size:0.72rem; color:var(--muted); font-weight:600; letter-spacing:0.06em; text-transform:uppercase; }
  .hero-cards { position:relative; height:480px; display:flex; align-items:center; justify-content:center; }
  .hero-card-stack { position:relative; width:300px; height:420px; }
  /* Back cards -- stacked deck effect */
  .hero-card-back { position:absolute; width:260px; background:white; border-radius:24px; box-shadow:0 16px 48px rgba(0,0,0,0.1); }
  .hero-card-back:nth-child(1) { top:30px; left:10px; height:380px; transform:rotate(-6deg); background:#f5e6e8; }
  .hero-card-back:nth-child(2) { top:18px; left:18px; height:380px; transform:rotate(-2deg); background:#faf0f1; }
  /* Front card -- Hinge profile style */
  .hero-card-front { position:absolute; top:0; left:0; width:270px; border-radius:24px; overflow:hidden; box-shadow:0 20px 60px rgba(0,0,0,0.18); z-index:5; background:var(--black); }
  .hero-card-front-img { width:100%; height:220px; background:linear-gradient(135deg,#E8355A,#0D0D0D); display:flex; align-items:center; justify-content:center; position:relative; overflow:hidden; }
  .hero-card-front-initials { font-family:'Playfair Display',serif; font-size:4rem; font-weight:900; color:white; opacity:0.35; }
  .hero-card-front-body { padding:1.25rem 1.25rem 1.5rem; }
  .hero-card-front-name { font-family:'Playfair Display',serif; font-size:1.3rem; font-weight:900; color:white; margin-bottom:0.2rem; }
  .hero-card-front-meta { font-size:0.75rem; color:#888; margin-bottom:0.85rem; }
  .hero-card-front-tags { display:flex; gap:0.35rem; flex-wrap:wrap; margin-bottom:0.85rem; }
  .hero-card-front-tag { font-size:0.65rem; font-weight:600; padding:0.2rem 0.55rem; border-radius:100px; background:#1e1e1e; color:#aaa; }
  .hero-card-front-prompt { background:#161616; border-radius:12px; padding:0.75rem; margin-bottom:0.75rem; }
  .hero-card-front-prompt-label { font-size:0.62rem; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; color:#555; margin-bottom:0.25rem; }
  .hero-card-front-prompt-text { font-size:0.78rem; color:#bbb; line-height:1.5; font-style:italic; }
  .hero-card-actions { display:flex; gap:0.5rem; }
  .hero-card-btn { flex:1; padding:0.6rem; border-radius:100px; border:none; font-family:'DM Sans',sans-serif; font-size:0.78rem; font-weight:700; cursor:pointer; }
  .hero-card-btn-pass { background:#1e1e1e; color:#666; }
  .hero-card-btn-connect { background:var(--rose); color:white; }
  .match-badge { position:absolute; top:1rem; right:1rem; background:var(--rose); color:white; font-size:0.62rem; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; padding:0.28rem 0.65rem; border-radius:100px; z-index:10; }

  .hero-paths { display:flex; flex-direction:column; gap:0.75rem; margin-top:2rem; }
  .hero-path { display:flex; align-items:center; gap:1rem; padding:1rem 1.25rem; background:white; border:1.5px solid var(--border); border-radius:14px; cursor:pointer; transition:all 0.2s; }
  .hero-path:hover { border-color:var(--rose); transform:translateX(4px); box-shadow:var(--card-shadow); }
  .hero-path-ai { opacity:0.65; cursor:default; }
  .hero-path-ai:hover { border-color:var(--border); transform:none; box-shadow:none; }
  .hero-path-icon { width:36px; height:36px; border-radius:10px; background:var(--cream); border:1px solid var(--border); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .hero-path-icon svg { width:16px; height:16px; stroke:var(--mid); }
  .hero-path-icon-ai { background:#0d0d0d; border-color:#222; }
  .hero-path-icon-ai svg { stroke:#666; }
  .hero-path-title { font-size:0.88rem; font-weight:700; color:var(--black); margin-bottom:0.15rem; display:flex; align-items:center; gap:0.5rem; }
  .hero-path-desc { font-size:0.75rem; color:var(--muted); font-weight:300; line-height:1.4; }
  .hero-path-arrow { margin-left:auto; font-size:0.9rem; color:var(--muted); flex-shrink:0; }
  .hero-path-soon { background:var(--black); color:var(--gold); font-size:0.6rem; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; padding:0.15rem 0.5rem; border-radius:100px; vertical-align:middle; }
  .hiw { background:var(--black); color:white; padding:5rem 2.5rem; }
  .hiw-inner { max-width:1200px; margin:0 auto; }
  .hiw-label { font-size:0.7rem; font-weight:700; letter-spacing:0.14em; text-transform:uppercase; color:var(--gold); margin-bottom:0.75rem; }
  .hiw-title { font-family:'Playfair Display',serif; font-size:clamp(1.8rem,3vw,2.8rem); font-weight:900; letter-spacing:-0.02em; margin-bottom:0.5rem; }
  .hiw-title em { font-style:italic; color:var(--rose); }
  .hiw-sub { color:#888; font-size:0.95rem; font-weight:300; margin-bottom:3.5rem; }
  .hiw-grid { display:grid; grid-template-columns:1fr auto 1fr auto 1fr; gap:1rem; align-items:center; }
  .hiw-step { padding:2rem; background:#161616; border-radius:20px; border:1px solid #222; transition:all 0.3s; }
  .hiw-step:hover { transform:translateY(-4px); border-color:#333; }
  .hiw-num { font-family:'Playfair Display',serif; font-size:3rem; font-weight:900; color:#222; line-height:1; margin-bottom:1rem; }
  .hiw-icon { font-size:1.8rem; margin-bottom:1rem; }
  .hiw-step-title { font-family:'Playfair Display',serif; font-size:1.2rem; font-weight:700; margin-bottom:0.6rem; }
  .hiw-step-desc { font-size:0.87rem; color:#888; line-height:1.65; font-weight:300; }
  .hiw-arrow { font-size:1.5rem; color:#333; text-align:center; }

  /* SECTION */
  .section { max-width:1200px; margin:0 auto; padding:3.5rem 2.5rem; }
  .section-label { font-size:0.7rem; font-weight:700; letter-spacing:0.14em; text-transform:uppercase; color:var(--rose); margin-bottom:0.6rem; }
  .section-title { font-family:'Playfair Display',serif; font-size:clamp(1.7rem,3vw,2.4rem); font-weight:900; letter-spacing:-0.02em; margin-bottom:0.4rem; }
  .section-sub { color:var(--muted); font-size:0.92rem; font-weight:300; margin-bottom:2rem; }

  /* FILTERS */
  .filters { display:flex; gap:0.65rem; flex-wrap:wrap; margin-bottom:1.75rem; padding:1.1rem 1.4rem; background:white; border-radius:16px; border:1px solid var(--border); align-items:center; }
  .filter-label { font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.1em; color:var(--muted); margin-right:0.25rem; }
  .filter-chip { padding:0.42rem 0.95rem; border-radius:100px; border:1.5px solid var(--border); background:transparent; font-family:'DM Sans',sans-serif; font-size:0.8rem; font-weight:500; cursor:pointer; transition:all 0.2s; color:var(--mid); }
  .filter-chip:hover,.filter-chip.active { border-color:var(--rose); color:var(--rose); background:rgba(232,53,90,0.05); }

  /* DEV CARDS */
  .dev-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(290px,1fr)); gap:1.4rem; }
  .dev-card { background:white; border-radius:20px; padding:1.6rem; border:1px solid var(--border); transition:all 0.3s; cursor:pointer; position:relative; overflow:hidden; }
  .dev-card::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:var(--accent,var(--rose)); transform:scaleX(0); transition:transform 0.3s; transform-origin:left; }
  .dev-card:hover { transform:translateY(-4px); box-shadow:0 16px 48px rgba(0,0,0,0.1); border-color:transparent; }
  .dev-card:hover::before { transform:scaleX(1); }
  .dev-card-top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1rem; }
  .dev-avatar { width:56px; height:56px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-size:1.1rem; font-weight:700; background:var(--accent-bg,#FFF0F3); color:var(--accent, var(--rose)); letter-spacing:-0.02em; overflow:hidden; flex-shrink:0; }
  .dev-avatar img { width:100%; height:100%; object-fit:cover; }
  .dev-badge { font-size:0.66rem; font-weight:700; letter-spacing:0.06em; text-transform:uppercase; padding:0.28rem 0.65rem; border-radius:100px; border:1.5px solid currentColor; }
  .badge-senior { color:var(--rose); } .badge-mid { color:#C77DFF; } .badge-junior { color:#06D6A0; }
  .dev-name { font-family:'Playfair Display',serif; font-size:1.15rem; font-weight:700; margin-bottom:0.2rem; }
  .dev-meta { font-size:0.78rem; color:var(--muted); margin-bottom:0.7rem; }
  .dev-bio { font-size:0.85rem; color:var(--mid); line-height:1.6; margin-bottom:0.75rem; font-weight:300; display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; }
  .dev-skills { display:flex; gap:0.38rem; flex-wrap:wrap; margin-bottom:0.9rem; }
  .skill-tag { font-size:0.7rem; font-weight:600; padding:0.27rem 0.65rem; border-radius:100px; background:var(--cream); color:var(--mid); }
  .dev-card-footer { display:flex; align-items:center; justify-content:space-between; padding-top:0.9rem; border-top:1px solid var(--border); }
  .dev-avail { font-size:0.73rem; font-weight:600; color:#06D6A0; display:flex; align-items:center; gap:0.3rem; }
  .dev-avail::before { content:''; width:5px; height:5px; background:#06D6A0; border-radius:50%; }
  .connect-btn { background:var(--black); color:white; border:none; padding:0.47rem 1rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.76rem; font-weight:600; cursor:pointer; transition:all 0.2s; }
  .connect-btn:hover { background:var(--rose); }
  .save-heart { background:none; border:none; font-size:1.1rem; cursor:pointer; transition:transform 0.2s; color:var(--muted); padding:0.2rem; }
  .save-heart:hover,.save-heart.saved { color:var(--rose); transform:scale(1.2); }

  /* PROJECT CARDS */
  .project-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(320px,1fr)); gap:1.4rem; }
  .project-card { background:white; border-radius:20px; border:1px solid var(--border); transition:all 0.3s; cursor:pointer; position:relative; overflow:hidden; }
  .project-card::after { content:''; position:absolute; bottom:0; left:0; right:0; height:3px; background:var(--p-accent,var(--rose)); transform:scaleX(0); transition:transform 0.35s; transform-origin:left; }
  .project-card:hover { transform:translateY(-4px); box-shadow:0 16px 48px rgba(0,0,0,0.1); border-color:transparent; }
  .project-card:hover::after { transform:scaleX(1); }
  .project-card-cover { width:100%; height:140px; object-fit:cover; display:block; }
  .project-card-cover-placeholder { width:100%; height:100px; background:linear-gradient(135deg,var(--p-accent-bg,#FFF0F3),white); display:flex; align-items:center; justify-content:center; }
  .project-card-body { padding:1.4rem; }
  .project-card-top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:0.9rem; }
  .project-emoji-wrap { width:50px; height:50px; border-radius:14px; display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-size:1rem; font-weight:700; letter-spacing:-0.02em; background:var(--p-accent-bg); color:var(--p-accent, var(--rose)); }
  .project-urgency { font-size:0.65rem; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; padding:0.27rem 0.65rem; border-radius:100px; }
  .urgency-asap { background:#FFF0F3; color:var(--rose); }
  .urgency-soon { background:#FFF8EC; color:#C9A84C; }
  .urgency-exploring { background:#F0FFF8; color:#06D6A0; }
  .project-name { font-family:'Playfair Display',serif; font-size:1.2rem; font-weight:700; margin-bottom:0.2rem; }
  .project-meta { font-size:0.78rem; color:var(--muted); margin-bottom:0.65rem; }
  .project-desc { font-size:0.85rem; color:var(--mid); line-height:1.6; margin-bottom:0.9rem; font-weight:300; display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; }
  .project-card-footer { display:flex; align-items:center; justify-content:space-between; padding-top:0.9rem; border-top:1px solid var(--border); }
  .paid-badge { font-size:0.72rem; font-weight:700; padding:0.27rem 0.65rem; border-radius:100px; }
  .paid-paid { background:#F0FFF8; color:#06D6A0; }
  .paid-equity { background:#F5F0FF; color:#C77DFF; }
  .paid-volunteer { background:var(--cream); color:var(--muted); }
  .apply-btn { background:var(--black); color:white; border:none; padding:0.47rem 1rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.76rem; font-weight:600; cursor:pointer; transition:all 0.2s; }
  .apply-btn:hover { background:var(--rose); }

  /* AI MATCH */
  .ai-section { background:linear-gradient(135deg,#0D0D0D 0%,#1a0812 100%); padding:4.5rem 2.5rem; }
  .ai-inner { max-width:1200px; margin:0 auto; }
  .ai-header { display:flex; align-items:flex-start; justify-content:space-between; gap:3rem; margin-bottom:2.5rem; flex-wrap:wrap; }
  .ai-label { display:inline-flex; align-items:center; gap:0.5rem; background:rgba(232,53,90,0.15); border:1px solid rgba(232,53,90,0.3); color:var(--rose); font-size:0.67rem; font-weight:700; letter-spacing:0.12em; text-transform:uppercase; padding:0.35rem 0.85rem; border-radius:100px; margin-bottom:0.75rem; }
  .ai-title { font-family:'Playfair Display',serif; font-size:clamp(1.6rem,2.5vw,2.6rem); font-weight:900; color:white; letter-spacing:-0.02em; }
  .ai-title em { font-style:italic; color:var(--rose); }
  .ai-sub { color:#777; font-size:0.9rem; font-weight:300; margin-top:0.5rem; }
  .ai-prompt-wrap { flex:1; min-width:280px; max-width:500px; }
  .ai-box { background:#161616; border:1.5px solid #2a2a2a; border-radius:16px; padding:1.25rem; transition:all 0.2s; }
  .ai-box:focus-within { border-color:var(--rose); box-shadow:0 0 0 3px rgba(232,53,90,0.1); }
  .ai-textarea { width:100%; background:transparent; border:none; outline:none; font-family:'DM Sans',sans-serif; font-size:0.88rem; color:white; line-height:1.6; resize:none; min-height:80px; font-weight:300; }
  .ai-textarea::placeholder { color:#444; }
  .ai-box-footer { display:flex; align-items:center; justify-content:space-between; margin-top:0.75rem; padding-top:0.75rem; border-top:1px solid #222; }
  .ai-hint { font-size:0.72rem; color:#555; }
  .ai-submit { background:var(--rose); color:white; border:none; padding:0.55rem 1.25rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.8rem; font-weight:700; cursor:pointer; transition:all 0.2s; }
  .ai-submit:hover { background:#c42047; transform:scale(1.03); }
  .ai-submit:disabled { opacity:0.5; cursor:not-allowed; transform:none; }
  .ai-loading { display:flex; align-items:center; gap:0.75rem; color:#666; font-size:0.85rem; padding:1.5rem 0; }
  .ai-dot { width:6px; height:6px; background:var(--rose); border-radius:50%; animation:aiDot 1.2s ease-in-out infinite; }
  .ai-dot:nth-child(2) { animation-delay:0.2s; }
  .ai-dot:nth-child(3) { animation-delay:0.4s; }
  @keyframes aiDot { 0%,80%,100%{transform:scale(0.6);opacity:0.4} 40%{transform:scale(1);opacity:1} }
  .ai-results { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1.2rem; margin-top:0.5rem; }
  .ai-card { background:#161616; border:1px solid #222; border-radius:16px; padding:1.4rem; position:relative; overflow:hidden; transition:all 0.3s; }
  .ai-card:hover { border-color:#333; transform:translateY(-2px); }
  .ai-score { position:absolute; top:1rem; right:1rem; background:var(--rose); color:white; font-size:0.66rem; font-weight:800; padding:0.25rem 0.6rem; border-radius:100px; }
  .ai-card-emoji { font-size:1.7rem; margin-bottom:0.65rem; }
  .ai-card-name { font-family:'Playfair Display',serif; font-size:1.05rem; font-weight:700; color:white; margin-bottom:0.2rem; }
  .ai-card-meta { font-size:0.75rem; color:#666; margin-bottom:0.7rem; }
  .ai-card-reason { font-size:0.82rem; color:#999; line-height:1.55; margin-bottom:1rem; font-weight:300; font-style:italic; }
  .ai-card-skills { display:flex; gap:0.35rem; flex-wrap:wrap; margin-bottom:1rem; }
  .ai-skill { font-size:0.68rem; font-weight:600; padding:0.22rem 0.55rem; border-radius:100px; background:#222; color:#888; }
  .ai-connect { width:100%; background:white; color:var(--black); border:none; padding:0.6rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.8rem; font-weight:700; cursor:pointer; transition:all 0.2s; }
  .ai-connect:hover { background:var(--rose); color:white; }
  .ai-coming-soon { display:grid; grid-template-columns:1fr 1fr; gap:4rem; align-items:start; }
  .ai-cs-left { padding-top:0.5rem; }
  .ai-cs-features { display:flex; flex-direction:column; gap:1.25rem; margin-top:2rem; }
  .ai-cs-feature { display:flex; align-items:flex-start; gap:1rem; }
  .ai-cs-feature-icon { width:36px; height:36px; border-radius:10px; background:#1e1e1e; border:1px solid #2a2a2a; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .ai-cs-feature-icon svg { width:16px; height:16px; stroke:#888; }
  .ai-cs-feature-title { font-size:0.88rem; font-weight:600; color:white; margin-bottom:0.2rem; }
  .ai-cs-feature-desc { font-size:0.78rem; color:#666; font-weight:300; }
  .ai-cs-right { position:relative; }
  .ai-cs-card { background:#161616; border:1px solid #2a2a2a; border-radius:20px; padding:2rem; }
  .ai-cs-card-label { font-size:0.68rem; font-weight:700; letter-spacing:0.12em; text-transform:uppercase; color:var(--rose); margin-bottom:0.6rem; }
  .ai-cs-card-title { font-family:'Playfair Display',serif; font-size:1.5rem; font-weight:900; color:white; line-height:1.15; margin-bottom:0.6rem; }
  .ai-cs-card-sub { font-size:0.83rem; color:#777; font-weight:300; margin-bottom:1.5rem; line-height:1.6; }
  .ai-cs-input { width:100%; background:#0d0d0d; border:1.5px solid #2a2a2a; border-radius:10px; padding:0.75rem 1rem; font-family:'DM Sans',sans-serif; font-size:0.88rem; color:white; outline:none; margin-bottom:0.65rem; transition:all 0.2s; }
  .ai-cs-input:focus { border-color:var(--rose); }
  .ai-cs-input::placeholder { color:#444; }
  .ai-cs-btn { width:100%; background:var(--rose); color:white; border:none; padding:0.82rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.88rem; font-weight:700; cursor:pointer; transition:all 0.2s; margin-bottom:1.75rem; }
  .ai-cs-btn:hover { background:#c42047; }
  .ai-cs-btn:disabled { opacity:0.4; cursor:not-allowed; }
  .ai-cs-success { background:#1a1a1a; border:1px solid #2a2a2a; border-radius:12px; padding:1.25rem; text-align:center; margin-bottom:1.75rem; color:white; font-size:0.88rem; }
  .ai-cs-preview-label { font-size:0.65rem; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:#444; margin-bottom:0.75rem; }
  .ai-cs-preview { position:relative; }
  .ai-cs-preview-card { background:#1e1e1e; border:1px solid #2a2a2a; border-radius:14px; padding:1.1rem; }
  .ai-cs-preview-score { display:inline-block; background:var(--rose); color:white; font-size:0.62rem; font-weight:800; padding:0.2rem 0.55rem; border-radius:100px; margin-bottom:0.75rem; }
  .ai-cs-preview-avatar { width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-size:0.85rem; font-weight:700; color:white; margin-bottom:0.6rem; }
  .ai-cs-preview-name { font-family:'Playfair Display',serif; font-size:0.95rem; font-weight:700; color:white; margin-bottom:0.15rem; }
  .ai-cs-preview-meta { font-size:0.72rem; color:#666; margin-bottom:0.5rem; }
  .ai-cs-preview-reason { font-size:0.75rem; color:#888; font-style:italic; line-height:1.5; margin-bottom:0.65rem; }
  .ai-cs-preview-blur { position:absolute; bottom:0; left:0; right:0; height:60px; background:linear-gradient(to bottom, transparent, #161616); border-radius:0 0 14px 14px; pointer-events:none; }
  @media (max-width:768px) { .ai-coming-soon { grid-template-columns:1fr; gap:2rem; } }

  /* FORMS */
  .form-card { background:white; border-radius:24px; padding:2.75rem; border:1px solid var(--border); max-width:700px; margin:0 auto; box-shadow:var(--card-shadow); }
  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:1.2rem; }
  .form-group { display:flex; flex-direction:column; gap:0.45rem; }
  .form-group.full { grid-column:1/-1; }
  .form-label { font-size:0.75rem; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:var(--mid); }
  .form-input,.form-select,.form-textarea { padding:0.82rem 1rem; border-radius:12px; border:1.5px solid var(--border); font-family:'DM Sans',sans-serif; font-size:0.9rem; color:var(--black); background:var(--cream); transition:all 0.2s; outline:none; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:var(--rose); background:white; box-shadow:0 0 0 3px rgba(232,53,90,0.07); }
  .form-textarea { resize:vertical; min-height:90px; }
  .skill-picker { display:flex; flex-wrap:wrap; gap:0.45rem; }
  .skill-pick-btn { padding:0.38rem 0.85rem; border-radius:100px; border:1.5px solid var(--border); background:transparent; font-family:'DM Sans',sans-serif; font-size:0.76rem; font-weight:500; cursor:pointer; transition:all 0.2s; color:var(--mid); }
  .skill-pick-btn.selected { border-color:var(--rose); background:var(--rose); color:white; }
  .skill-group { margin-bottom:0.25rem; }
  .skill-group-label { font-size:0.62rem; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:var(--muted); margin-bottom:0.4rem; margin-top:0.85rem; display:block; }
  .skill-group:first-child .skill-group-label { margin-top:0; }
  .skill-group-pills { display:flex; flex-wrap:wrap; gap:0.4rem; }
  .skill-mode-toggle { margin-bottom:0.75rem; }
  .skill-mode-card { display:flex; align-items:center; gap:1rem; padding:1rem 1.25rem; background:var(--cream); border:1.5px dashed var(--border); border-radius:14px; cursor:pointer; transition:all 0.2s; }
  .skill-mode-card:hover { border-color:var(--rose); border-style:solid; background:white; }
  .skill-mode-card-icon { width:36px; height:36px; border-radius:10px; background:white; border:1px solid var(--border); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .skill-mode-card-icon svg { width:16px; height:16px; stroke:var(--rose); }
  .skill-mode-card-title { font-size:0.88rem; font-weight:700; color:var(--black); margin-bottom:0.15rem; }
  .skill-mode-card-desc { font-size:0.75rem; color:var(--muted); font-weight:300; line-height:1.4; }
  .skill-wizard { background:var(--cream); border:1.5px solid var(--border); border-radius:16px; padding:1.5rem; margin-bottom:0.75rem; }
  .skill-wizard-header { margin-bottom:1.25rem; }
  .skill-wizard-step { font-size:0.68rem; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:var(--muted); margin-bottom:0.5rem; }
  .skill-wizard-progress { height:3px; background:var(--border); border-radius:100px; overflow:hidden; }
  .skill-wizard-bar { height:100%; background:var(--rose); border-radius:100px; transition:width 0.3s ease; }
  .skill-wizard-q { font-family:'Playfair Display',serif; font-size:1.1rem; font-weight:700; margin-bottom:1rem; line-height:1.3; }
  .skill-wizard-options { display:flex; flex-direction:column; gap:0.5rem; margin-bottom:1rem; }
  .skill-wizard-opt { text-align:left; padding:0.75rem 1rem; background:white; border:1.5px solid var(--border); border-radius:12px; font-family:'DM Sans',sans-serif; font-size:0.88rem; font-weight:500; cursor:pointer; transition:all 0.2s; color:var(--black); }
  .skill-wizard-opt:hover { border-color:var(--rose); color:var(--rose); background:#FFF0F3; }
  .skill-wizard-skip { background:none; border:none; font-family:'DM Sans',sans-serif; font-size:0.75rem; color:var(--muted); cursor:pointer; text-decoration:underline; padding:0; transition:color 0.2s; display:block; }
  .skill-wizard-skip:hover { color:var(--black); }
  .skill-selected-summary { background:var(--cream); border:1px solid var(--border); border-radius:12px; padding:1rem; margin-bottom:0.5rem; }
  .skill-selected-label { font-size:0.72rem; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:var(--muted); }
  .skill-custom-add { display:flex; gap:0.5rem; margin-top:1rem; align-items:center; }
  .skill-custom-input { flex:1; padding:0.55rem 0.9rem; border-radius:100px; border:1.5px dashed var(--border); font-family:'DM Sans',sans-serif; font-size:0.8rem; color:var(--black); background:var(--cream); outline:none; transition:all 0.2s; }
  .skill-custom-input:focus { border-color:var(--rose); background:white; border-style:solid; }
  .skill-custom-input::placeholder { color:var(--muted); }
  .skill-custom-btn { background:var(--black); color:white; border:none; padding:0.55rem 1.1rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.78rem; font-weight:600; cursor:pointer; transition:all 0.2s; white-space:nowrap; }
  .skill-custom-btn:hover { background:var(--rose); }
  .skill-custom-btn:disabled { opacity:0.35; cursor:not-allowed; }
  .form-submit { width:100%; background:var(--rose); color:white; border:none; padding:0.95rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.97rem; font-weight:700; cursor:pointer; transition:all 0.25s; margin-top:1rem; }
  .form-submit:hover { background:#c42047; transform:translateY(-2px); box-shadow:0 8px 24px rgba(232,53,90,0.28); }

  /* SUCCESS */
  .success-state { text-align:center; padding:3rem; }
  .success-icon { font-size:3rem; margin-bottom:1rem; animation:bounce 0.6s ease; }
  @keyframes bounce { 0%,100%{transform:translateY(0)} 40%{transform:translateY(-16px)} 60%{transform:translateY(-8px)} }
  .success-title { font-family:'Playfair Display',serif; font-size:1.8rem; font-weight:900; margin-bottom:0.75rem; }
  .success-sub { color:var(--muted); font-size:0.92rem; line-height:1.6; max-width:380px; margin:0 auto 1.5rem; font-weight:300; }

  /* SAVED PANEL */
  .panel-overlay { position:fixed; inset:0; background:rgba(13,13,13,0.55); backdrop-filter:blur(4px); z-index:200; display:flex; justify-content:flex-end; animation:fadeIn 0.2s; }
  .panel { background:white; width:100%; max-width:400px; height:100%; overflow-y:auto; padding:2rem; animation:slideRight 0.3s ease; }
  @keyframes slideRight { from{transform:translateX(100%)} to{transform:translateX(0)} }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  .panel-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:2rem; }
  .panel-title { font-family:'Playfair Display',serif; font-size:1.5rem; font-weight:900; }
  .close-btn { background:var(--cream); border:none; width:34px; height:34px; border-radius:50%; cursor:pointer; font-size:1rem; display:flex; align-items:center; justify-content:center; color:var(--mid); transition:all 0.2s; }
  .close-btn:hover { background:var(--black); color:white; }
  .saved-item { display:flex; align-items:center; gap:1rem; padding:1rem; border:1px solid var(--border); border-radius:14px; margin-bottom:0.75rem; transition:all 0.2s; }
  .saved-item:hover { border-color:var(--rose); }
  .saved-item-avatar { font-family:'Playfair Display',serif; font-size:0.9rem; font-weight:700; width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; background:var(--rose); color:white; flex-shrink:0; }
  .saved-item-info { flex:1; }
  .saved-item-name { font-weight:600; font-size:0.9rem; margin-bottom:0.15rem; }
  .saved-item-meta { font-size:0.75rem; color:var(--muted); }
  .unsave-btn { background:none; border:none; font-size:1rem; cursor:pointer; color:var(--rose); transition:transform 0.2s; }
  .unsave-btn:hover { transform:scale(1.2); }
  .empty-saved { text-align:center; padding:3rem 1rem; color:var(--muted); }
  .empty-saved-icon { width:48px; height:48px; border:2px solid var(--border); border-radius:50%; display:flex; align-items:center; justify-content:center; margin:0 auto 1rem; }
  .empty-saved-icon svg { width:22px; height:22px; stroke:var(--muted); fill:none; stroke-width:1.5; }
  .empty-saved-text { font-size:0.9rem; line-height:1.6; }
  .ai-card-avatar { width:40px; height:40px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-size:1rem; font-weight:700; color:white; margin-bottom:0.65rem; }
  .comms-style { display:inline-flex; align-items:center; gap:0.4rem; font-size:0.72rem; font-weight:600; color:var(--mid); background:var(--cream); padding:0.28rem 0.65rem; border-radius:100px; border:1px solid var(--border); margin-bottom:0.7rem; }
  .comms-style svg { width:11px; height:11px; stroke:var(--muted); fill:none; stroke-width:2; flex-shrink:0; }
  .success-icon { font-size:2rem; font-family:'Playfair Display',serif; font-weight:900; color:var(--rose); margin-bottom:1rem; animation:bounce 0.6s ease; letter-spacing:-0.03em; }

  /* AUTH */
  .auth-overlay { position:fixed; inset:0; background:rgba(13,13,13,0.7); backdrop-filter:blur(6px); z-index:400; display:flex; align-items:center; justify-content:center; padding:1.5rem; animation:fadeIn 0.2s; }
  .auth-box { background:white; border-radius:24px; padding:2.5rem; max-width:420px; width:100%; position:relative; animation:slideUp 0.3s ease; }
  .auth-logo { font-family:'Playfair Display',serif; font-size:1.2rem; font-weight:900; margin-bottom:0.25rem; }
  .auth-logo span { color:var(--rose); font-style:italic; }
  .auth-title { font-family:'Playfair Display',serif; font-size:1.6rem; font-weight:900; margin-bottom:0.4rem; margin-top:1.25rem; }
  .auth-sub { font-size:0.85rem; color:var(--muted); margin-bottom:1.75rem; font-weight:300; line-height:1.6; }
  .auth-role-pick { display:grid; grid-template-columns:1fr 1fr; gap:0.75rem; margin-bottom:1.5rem; }
  .auth-role-btn { padding:1rem; border:1.5px solid var(--border); border-radius:14px; background:var(--cream); cursor:pointer; transition:all 0.2s; text-align:center; }
  .auth-role-btn:hover, .auth-role-btn.active { border-color:var(--rose); background:#FFF0F3; }
  .auth-role-icon { font-size:1.5rem; margin-bottom:0.4rem; }
  .auth-role-label { font-size:0.82rem; font-weight:700; color:var(--black); }
  .auth-role-desc { font-size:0.7rem; color:var(--muted); margin-top:0.15rem; }
  .auth-input { width:100%; padding:0.82rem 1rem; border-radius:12px; border:1.5px solid var(--border); font-family:'DM Sans',sans-serif; font-size:0.9rem; color:var(--black); background:var(--cream); transition:all 0.2s; outline:none; margin-bottom:0.75rem; }
  .auth-input:focus { border-color:var(--rose); background:white; box-shadow:0 0 0 3px rgba(232,53,90,0.07); }
  .auth-btn { width:100%; background:var(--rose); color:white; border:none; padding:0.92rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.95rem; font-weight:700; cursor:pointer; transition:all 0.25s; margin-top:0.25rem; }
  .auth-btn:hover { background:#c42047; }
  .auth-btn:disabled { opacity:0.5; cursor:not-allowed; }
  .auth-switch { text-align:center; margin-top:1rem; font-size:0.82rem; color:var(--muted); }
  .auth-switch button { background:none; border:none; color:var(--rose); font-weight:700; cursor:pointer; font-family:'DM Sans',sans-serif; font-size:0.82rem; }
  .auth-error { background:#FFF0F3; color:var(--rose); font-size:0.82rem; padding:0.65rem 0.9rem; border-radius:10px; margin-bottom:0.75rem; }
  .auth-divider { display:flex; align-items:center; gap:0.75rem; margin:1rem 0; color:var(--muted); font-size:0.75rem; }
  .auth-divider::before, .auth-divider::after { content:''; flex:1; height:1px; background:var(--border); }

  /* DASHBOARD */
  .dashboard { max-width:900px; margin:0 auto; padding:3rem 2.5rem; }
  .dashboard-header { display:flex; align-items:center; gap:1.5rem; margin-bottom:2.5rem; padding-bottom:2rem; border-bottom:1px solid var(--border); }
  .dashboard-avatar { width:72px; height:72px; border-radius:18px; object-fit:cover; background:var(--rose); display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-size:1.6rem; font-weight:700; color:white; flex-shrink:0; overflow:hidden; }
  .dashboard-avatar img { width:100%; height:100%; object-fit:cover; }
  .dashboard-name { font-family:'Playfair Display',serif; font-size:1.6rem; font-weight:900; margin-bottom:0.2rem; }
  .dashboard-role { display:inline-flex; align-items:center; gap:0.4rem; font-size:0.72rem; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; color:var(--rose); background:#FFF0F3; padding:0.28rem 0.7rem; border-radius:100px; }
  .dashboard-tabs { display:flex; gap:0.25rem; background:var(--black); border-radius:100px; padding:0.28rem; margin-bottom:2rem; width:fit-content; }
  .dashboard-tab { padding:0.45rem 1.1rem; border-radius:100px; border:none; font-family:'DM Sans',sans-serif; font-size:0.8rem; font-weight:500; cursor:pointer; transition:all 0.2s; background:transparent; color:#777; }
  .dashboard-tab.active { background:var(--rose); color:white; }
  .dash-card { background:white; border:1px solid var(--border); border-radius:16px; padding:1.5rem; margin-bottom:1rem; transition:all 0.2s; }
  .dash-card:hover { border-color:var(--rose); }
  .dash-card-title { font-family:'Playfair Display',serif; font-size:1.05rem; font-weight:700; margin-bottom:0.25rem; }
  .dash-card-meta { font-size:0.78rem; color:var(--muted); margin-bottom:0.65rem; }
  .dash-card-actions { display:flex; gap:0.65rem; margin-top:1rem; }
  .dash-btn { padding:0.5rem 1.1rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.78rem; font-weight:600; cursor:pointer; transition:all 0.2s; border:1.5px solid var(--border); background:transparent; color:var(--mid); }
  .dash-btn:hover { border-color:var(--rose); color:var(--rose); }
  .dash-btn-primary { background:var(--black); color:white; border-color:var(--black); }
  .dash-btn-primary:hover { background:var(--rose); border-color:var(--rose); color:white; }
  .interest-item { display:flex; align-items:center; gap:1rem; padding:1rem; border:1px solid var(--border); border-radius:12px; margin-bottom:0.65rem; }
  .interest-avatar { width:40px; height:40px; border-radius:10px; background:var(--rose); display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-weight:700; color:white; font-size:0.9rem; flex-shrink:0; overflow:hidden; }
  .interest-avatar img { width:100%; height:100%; object-fit:cover; }
  .interest-name { font-weight:600; font-size:0.88rem; margin-bottom:0.15rem; }
  .interest-meta { font-size:0.75rem; color:var(--muted); }
  .interest-msg { font-size:0.8rem; color:var(--mid); font-style:italic; margin-top:0.35rem; line-height:1.5; }
  .empty-dash { text-align:center; padding:3rem; color:var(--muted); }
  .empty-dash-title { font-family:'Playfair Display',serif; font-size:1.1rem; font-weight:700; color:var(--black); margin-bottom:0.5rem; }
  .empty-dash-sub { font-size:0.85rem; line-height:1.6; }

  /* AVATAR UPLOAD */
  .avatar-upload { display:flex; align-items:center; gap:1rem; margin-bottom:1.25rem; }
  .avatar-preview { width:64px; height:64px; border-radius:14px; background:var(--cream); border:1.5px solid var(--border); display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-size:1.2rem; font-weight:700; color:var(--muted); overflow:hidden; flex-shrink:0; }
  .avatar-preview img { width:100%; height:100%; object-fit:cover; }
  .avatar-upload-btn { padding:0.55rem 1.1rem; border-radius:100px; border:1.5px solid var(--border); background:transparent; font-family:'DM Sans',sans-serif; font-size:0.78rem; font-weight:600; cursor:pointer; transition:all 0.2s; color:var(--mid); }
  .avatar-upload-btn:hover { border-color:var(--rose); color:var(--rose); }

  /* INTEREST MODAL */
  .interest-modal { background:white; border-radius:24px; padding:2.25rem; max-width:420px; width:100%; position:relative; animation:slideUp 0.3s ease; }
  .interest-textarea { width:100%; padding:0.85rem 1rem; border-radius:12px; border:1.5px solid var(--border); font-family:'DM Sans',sans-serif; font-size:0.9rem; color:var(--black); background:var(--cream); outline:none; resize:none; min-height:100px; margin:0.75rem 0 1rem; transition:all 0.2s; }
  .interest-textarea:focus { border-color:var(--rose); background:white; }

  /* MOBILE */
  @media (max-width:768px) { .dashboard { padding:2rem 1.25rem; } .auth-role-pick { grid-template-columns:1fr; } .dashboard-header { flex-direction:column; align-items:flex-start; } }

  /* MODAL */
  .modal-overlay { position:fixed; inset:0; background:rgba(13,13,13,0.65); backdrop-filter:blur(5px); z-index:300; display:flex; align-items:center; justify-content:center; padding:1.5rem; animation:fadeIn 0.2s; }
  .modal { background:white; border-radius:24px; padding:2.25rem; max-width:460px; width:100%; position:relative; animation:slideUp 0.3s ease; max-height:90vh; overflow-y:auto; }
  @keyframes slideUp { from{transform:translateY(20px);opacity:0} to{transform:translateY(0);opacity:1} }
  .modal-close { position:absolute; top:1.1rem; right:1.1rem; background:var(--cream); border:none; width:32px; height:32px; border-radius:50%; cursor:pointer; font-size:0.9rem; display:flex; align-items:center; justify-content:center; color:var(--mid); transition:all 0.2s; }
  .modal-close:hover { background:var(--black); color:white; }
  .modal-emoji { font-size:2.5rem; margin-bottom:0.85rem; }
  .modal-name { font-family:'Playfair Display',serif; font-size:1.55rem; font-weight:900; margin-bottom:0.2rem; }
  .modal-meta { font-size:0.8rem; color:var(--muted); margin-bottom:1.1rem; }
  .modal-bio { font-size:0.9rem; color:var(--mid); line-height:1.7; margin-bottom:1.25rem; font-weight:300; }
  .modal-skills { display:flex; flex-wrap:wrap; gap:0.45rem; margin-bottom:1.25rem; }
  .modal-portfolio { font-size:0.8rem; font-weight:600; color:var(--rose); text-decoration:none; display:inline-flex; align-items:center; gap:0.3rem; margin-bottom:1.25rem; }
  .modal-actions { display:flex; gap:0.65rem; }
  .modal-connect { flex:1; background:var(--rose); color:white; border:none; padding:0.8rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-weight:700; font-size:0.88rem; cursor:pointer; transition:all 0.2s; }
  .modal-connect:hover { background:#c42047; }
  .modal-save-btn { background:var(--cream); color:var(--black); border:none; padding:0.8rem 1.2rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-weight:600; font-size:0.88rem; cursor:pointer; transition:all 0.2s; }
  .modal-save-btn:hover { background:var(--black); color:white; }
  .modal-save-btn.is-saved { background:#FFF0F3; color:var(--rose); }
  .no-results { color:var(--muted); font-size:0.9rem; padding:2rem 0; }

  /* PAST PROJECTS */
  .past-projects { margin-bottom: 0.9rem; }
  .past-projects-label { font-size: 0.68rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); margin-bottom: 0.5rem; display: flex; align-items: center; gap: 0.4rem; }
  .past-projects-label::before { content: ''; width: 14px; height: 1.5px; background: var(--muted); }
  .project-pills { display: flex; flex-direction: column; gap: 0.4rem; }
  .project-pill { display: flex; align-items: center; gap: 0.5rem; padding: 0.45rem 0.7rem; background: var(--cream); border-radius: 10px; border: 1px solid var(--border); transition: all 0.2s; }
  .project-pill:hover { border-color: var(--accent, var(--rose)); background: white; }
  .project-pill-emoji { font-size: 0.9rem; flex-shrink: 0; }
  .project-pill-info { flex: 1; min-width: 0; }
  .project-pill-name { font-size: 0.78rem; font-weight: 700; color: var(--black); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .project-pill-desc { font-size: 0.7rem; color: var(--muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .project-pill-industry { font-size: 0.65rem; font-weight: 600; color: var(--accent, var(--rose)); flex-shrink: 0; }

  /* COMMS STYLE */
  .comms-style { display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.72rem; font-weight: 600; color: var(--mid); background: var(--cream); padding: 0.28rem 0.65rem; border-radius: 100px; border: 1px solid var(--border); margin-bottom: 0.7rem; }

  /* PORTFOLIO LINK ON CARD */
  .portfolio-link-card { display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.75rem; font-weight: 700; color: var(--accent, var(--rose)); text-decoration: none; margin-bottom: 0.75rem; transition: opacity 0.2s; }
  .portfolio-link-card:hover { opacity: 0.75; }

  /* MODAL PAST PROJECTS */
  .modal-projects { margin-bottom: 1.25rem; }
  .modal-projects-title { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); margin-bottom: 0.65rem; }
  .modal-project-item { display: flex; align-items: flex-start; gap: 0.75rem; padding: 0.85rem; background: var(--cream); border-radius: 12px; margin-bottom: 0.5rem; border: 1px solid var(--border); }
  .modal-project-name { font-size: 0.88rem; font-weight: 700; margin-bottom: 0.15rem; }
  .modal-project-desc { font-size: 0.8rem; color: var(--muted); line-height: 1.4; font-weight: 300; }
  .modal-project-tag { font-size: 0.65rem; font-weight: 700; color: var(--rose); margin-top: 0.25rem; letter-spacing: 0.05em; text-transform: uppercase; }
  .modal-emoji { display: none; }

  /* SAVED PANEL */
  .saved-item-emoji { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; background:var(--cream); font-family:'Playfair Display',serif; font-size:0.85rem; font-weight:700; color:var(--rose); flex-shrink:0; }
  .empty-saved-icon { font-size:1.5rem; font-family:'Playfair Display',serif; font-weight:900; color:var(--border); margin-bottom:1rem; }
  .success-icon { font-size:1.5rem; font-family:'Playfair Display',serif; font-weight:900; color:var(--rose); margin-bottom:1rem; animation:bounce 0.6s ease; }

  /* TRUST BANNER */
  .trust-banner { background: linear-gradient(135deg, #0D0D0D, #1a0812); padding: 2.5rem; border-radius: 20px; margin-bottom: 2rem; display: flex; align-items: center; gap: 2rem; flex-wrap: wrap; }
  .trust-banner-text { flex: 1; min-width: 200px; }
  .trust-banner-title { font-family: 'Playfair Display', serif; font-size: 1.2rem; font-weight: 900; color: white; margin-bottom: 0.3rem; }
  .trust-banner-sub { font-size: 0.82rem; color: #888; font-weight: 300; }
  .trust-pills { display: flex; gap: 0.5rem; flex-wrap: wrap; }
  .trust-pill { display: flex; align-items: center; gap: 0.35rem; background: #1e1e1e; border: 1px solid #2a2a2a; color: #ccc; font-size: 0.75rem; font-weight: 600; padding: 0.4rem 0.85rem; border-radius: 100px; }

  .footer { border-top:1px solid var(--border); padding:2rem 2.5rem; display:flex; align-items:center; justify-content:space-between; max-width:1200px; margin:0 auto; }
  .footer-logo { font-family:'Playfair Display',serif; font-size:1.05rem; font-weight:900; }
  .footer-logo span { color:var(--rose); font-style:italic; }
  .footer-text { font-size:0.75rem; color:var(--muted); }

  /* RESPONSIVE */
  @media (max-width:768px) {
    .hero { grid-template-columns:1fr; gap:2rem; padding:2.5rem 1.25rem; }
    .hero-cards { display:block; transform:scale(0.85); transform-origin:center top; margin:-2rem auto 0; }
    .hiw-grid { grid-template-columns:1fr; }
    .hiw-arrow { display:none; }
    .form-grid { grid-template-columns:1fr; }
    .nav { padding:0.9rem 1.25rem; }
    .nav-tabs { display:none; }
    .mobile-nav { display:block; }
    .section { padding:2.5rem 1.25rem; }
    .search-bar-wrap { padding:1.25rem 1.25rem 0; }
    .form-card { padding:1.5rem; }
    .footer { flex-direction:column; gap:0.75rem; text-align:center; }
    .hiw { padding:3.5rem 1.25rem; }
    .ai-section { padding:3rem 1.25rem; }
    .ai-header { flex-direction:column; }
    .nav-right .saved-btn { display:none; }
    .app { padding-bottom:85px; }
    .dev-grid { grid-template-columns:1fr; }
    .project-grid { grid-template-columns:1fr; }
    .modal { max-height:85vh; overflow-y:auto; }
    .dashboard { padding:1.5rem 1.25rem; }
    .dash-card { padding:1.25rem; }
    .hero-card-stack { transform:scale(0.9); }
  }

  /* HERO ILLUSTRATION */
  .hero-illustration { display:flex; align-items:center; justify-content:center; height:100%; }

  /* SKELETON LOADER */
  .skeleton { background:linear-gradient(90deg,#f0ede8 25%,#e8e4de 50%,#f0ede8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:8px; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton-card { background:white; border-radius:20px; padding:1.6rem; border:1px solid var(--border); }
  .skeleton-avatar { width:50px; height:50px; border-radius:14px; margin-bottom:1rem; }
  .skeleton-line { height:14px; margin-bottom:0.6rem; }
  .skeleton-line.short { width:40%; }
  .skeleton-line.med { width:65%; }
  .skeleton-line.long { width:90%; }

  /* ADMIN */
  .admin-gate { min-height:100vh; display:flex; align-items:center; justify-content:center; background:var(--black); }
  .admin-gate-box { background:#161616; border:1px solid #2a2a2a; border-radius:24px; padding:2.5rem; max-width:380px; width:100%; text-align:center; }
  .admin-gate-logo { font-family:'Playfair Display',serif; font-size:1.3rem; font-weight:900; color:white; margin-bottom:0.25rem; }
  .admin-gate-logo span { color:var(--rose); font-style:italic; }
  .admin-gate-title { font-size:0.85rem; color:#666; margin-bottom:1.5rem; }
  .admin { max-width:1100px; margin:0 auto; padding:2.5rem; }
  .admin-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:2rem; }
  .admin-title { font-family:'Playfair Display',serif; font-size:1.8rem; font-weight:900; }
  .admin-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; margin-bottom:2rem; }
  .admin-stat { background:white; border:1px solid var(--border); border-radius:16px; padding:1.25rem; text-align:center; }
  .admin-stat-num { font-family:'Playfair Display',serif; font-size:2rem; font-weight:900; color:var(--rose); }
  .admin-stat-label { font-size:0.75rem; color:var(--muted); font-weight:600; letter-spacing:0.05em; text-transform:uppercase; margin-top:0.25rem; }
  .admin-tabs { display:flex; gap:0.25rem; background:var(--black); border-radius:100px; padding:0.28rem; width:fit-content; margin-bottom:1.5rem; }
  .admin-tab { padding:0.45rem 1.1rem; border-radius:100px; border:none; font-family:'DM Sans',sans-serif; font-size:0.8rem; font-weight:500; cursor:pointer; transition:all 0.2s; background:transparent; color:#777; }
  .admin-tab.active { background:var(--rose); color:white; }
  .admin-table { width:100%; border-collapse:collapse; background:white; border-radius:16px; overflow:hidden; border:1px solid var(--border); }
  .admin-table th { text-align:left; padding:0.85rem 1rem; font-size:0.7rem; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; color:var(--muted); border-bottom:1px solid var(--border); background:var(--cream); }
  .admin-table td { padding:0.85rem 1rem; font-size:0.85rem; border-bottom:1px solid var(--border); vertical-align:top; }
  .admin-table tr:last-child td { border-bottom:none; }
  .admin-table tr:hover td { background:#fafafa; }
  .admin-badge { display:inline-block; font-size:0.65rem; font-weight:700; padding:0.2rem 0.55rem; border-radius:100px; }
  .admin-badge-dev { background:#FFF0F3; color:var(--rose); }
  .admin-badge-owner { background:#F5F0FF; color:#C77DFF; }
  .admin-action { background:none; border:1.5px solid var(--border); padding:0.3rem 0.7rem; border-radius:100px; font-family:'DM Sans',sans-serif; font-size:0.72rem; font-weight:600; cursor:pointer; transition:all 0.2s; color:var(--mid); margin-right:0.35rem; }
  .admin-action:hover { border-color:var(--rose); color:var(--rose); }
  .admin-action.danger:hover { border-color:#ff4444; color:#ff4444; }
  @media(max-width:768px) { .admin-stats { grid-template-columns:1fr 1fr; } .admin { padding:1.25rem; } }

  /* TERMS & PRIVACY */
  .legal { max-width:720px; margin:0 auto; padding:3.5rem 2.5rem; }
  .legal h1 { font-family:'Playfair Display',serif; font-size:2.2rem; font-weight:900; margin-bottom:0.5rem; }
  .legal-date { font-size:0.82rem; color:var(--muted); margin-bottom:2.5rem; }
  .legal h2 { font-family:'Playfair Display',serif; font-size:1.15rem; font-weight:700; margin:2rem 0 0.5rem; }
  .legal p { font-size:0.9rem; color:var(--mid); line-height:1.8; margin-bottom:0.75rem; font-weight:300; }
  .legal ul { padding-left:1.25rem; margin-bottom:0.75rem; }
  .legal li { font-size:0.9rem; color:var(--mid); line-height:1.8; font-weight:300; }
  @media(max-width:768px) { .legal { padding:2rem 1.25rem; } }

  /* CONTACT */
  .contact-wrap { max-width:580px; margin:0 auto; padding:3.5rem 2.5rem; text-align:center; }
  .contact-title { font-family:'Playfair Display',serif; font-size:2rem; font-weight:900; margin-bottom:0.5rem; }
  .contact-sub { color:var(--muted); font-size:0.95rem; line-height:1.7; margin-bottom:2rem; font-weight:300; }
  .contact-card { background:white; border:1px solid var(--border); border-radius:20px; padding:2rem; margin-bottom:1rem; transition:all 0.2s; text-decoration:none; display:block; }
  .contact-card:hover { border-color:var(--rose); transform:translateY(-2px); box-shadow:var(--card-shadow); }
  .contact-card-title { font-weight:700; font-size:0.95rem; color:var(--black); margin-bottom:0.25rem; }
  .contact-card-sub { font-size:0.82rem; color:var(--muted); }
  @media(max-width:768px) { .contact-wrap { padding:2rem 1.25rem; } }

  /* PASSWORD RESET */
  .reset-wrap { min-height:60vh; display:flex; align-items:center; justify-content:center; padding:2rem; }
`;

// ── GROUPED SKILL PICKER ─────────────────────────────────────────────────────
function GroupedSkillPicker({ selected, onToggle }) {
  const [custom, setCustom] = useState("");

  const handleAdd = () => {
    const trimmed = custom.trim();
    if (trimmed && !selected.includes(trimmed)) {
      onToggle(trimmed);
    }
    setCustom("");
  };

  // Custom skills = selected items not in the master SKILLS list
  const customSelected = selected.filter(s => !SKILLS.includes(s));

  return (
    <div>
      {SKILL_GROUPS.map(group => (
        <div key={group.label} className="skill-group">
          <span className="skill-group-label">{group.label}</span>
          <div className="skill-group-pills">
            {group.skills.map(s => (
              <button
                key={s}
                className={`skill-pick-btn ${selected.includes(s) ? "selected" : ""}`}
                onClick={() => onToggle(s)}
              >{s}</button>
            ))}
          </div>
        </div>
      ))}

      {/* Custom skills already added */}
      {customSelected.length > 0 && (
        <div className="skill-group">
          <span className="skill-group-label">Your additions</span>
          <div className="skill-group-pills">
            {customSelected.map(s => (
              <button key={s} className="skill-pick-btn selected" onClick={() => onToggle(s)}>
                {s} ✕
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Add custom skill */}
      <div className="skill-custom-add">
        <input
          className="skill-custom-input"
          placeholder="Don't see yours? Add it here..."
          value={custom}
          onChange={e => setCustom(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleAdd()}
        />
        <button className="skill-custom-btn" onClick={handleAdd} disabled={!custom.trim()}>
          + Add
        </button>
      </div>
    </div>
  );
}

// ── COMPONENTS ────────────────────────────────────────────────────────────────

function DevModal({ dev, saved, onSave, onClose, onConnect, onNavigate, user }) {
  const [fullPage, setFullPage] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  if (!dev) return null;

  const isOwn = user && dev.user_id && user.id === dev.user_id;

  const avatar = dev.avatar_url
    ? <img src={dev.avatar_url} alt={dev.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
    : initials(dev.name);

  const links = [
    { url: dev.portfolio_url || dev.portfolio, label: "Portfolio" },
    { url: dev.linkedin_url, label: "LinkedIn" },
    { url: dev.github_url, label: "GitHub" },
    { url: dev.instagram_url, label: "Instagram" },
    { url: dev.other_url, label: "Other" },
  ].filter(l => l.url);

  if (fullPage) return (
    <div style={{position:"fixed",inset:0,background:"var(--cream)",zIndex:1000,overflowY:"auto"}}>
      {/* Full nav bar */}
      <div style={{position:"sticky",top:0,background:"white",borderBottom:"1px solid var(--border)",zIndex:10,padding:"0 2rem",display:"flex",alignItems:"center",justifyContent:"space-between",height:"60px",boxShadow:"0 1px 12px rgba(0,0,0,0.04)"}}>
        <button onClick={()=>setFullPage(false)} style={{display:"flex",alignItems:"center",gap:"0.4rem",background:"none",border:"none",cursor:"pointer",color:"var(--muted)",fontFamily:"'DM Sans',sans-serif",fontSize:"0.82rem",fontWeight:600,flexShrink:0}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Back
        </button>
        <div style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:"1.05rem",cursor:"pointer"}} onClick={()=>{onClose();}}>The Match<span style={{color:"var(--rose)",fontStyle:"italic"}}>Deck</span></div>
        {isOwn
          ? <button onClick={()=>{onClose();onNavigate("dashboard");}} style={{fontSize:"0.78rem",fontWeight:700,background:"var(--rose)",color:"white",border:"none",borderRadius:"100px",padding:"0.4rem 1rem",cursor:"pointer",flexShrink:0}}>Edit profile</button>
          : <button className={`save-heart ${saved?"is-saved":""}`} onClick={()=>onSave(dev)} style={{fontSize:"1.2rem",background:"none",border:"none",cursor:"pointer",flexShrink:0}}>{saved?"♥":"♡"}</button>
        }
      </div>
      <div style={{maxWidth:"720px",margin:"0 auto",padding:"2rem 1.5rem"}}>
        <div style={{display:"flex",alignItems:"flex-start",gap:"1.5rem",marginBottom:"2rem",flexWrap:"wrap"}}>
          <div style={{width:"88px",height:"88px",borderRadius:"20px",background:dev.accent||"var(--rose)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Playfair Display',serif",fontWeight:700,color:"white",fontSize:"1.8rem",flexShrink:0,overflow:"hidden"}}>{avatar}</div>
          <div style={{flex:1}}>
            <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"1.8rem",fontWeight:900,marginBottom:"0.3rem"}}>{dev.name}</h1>
            <div style={{color:"var(--muted)",fontSize:"0.88rem",marginBottom:"0.75rem"}}>{dev.level} · {dev.city} · {dev.available||dev.availability}</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:"0.5rem"}}>
              {links.map(l=>(
                <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer"
                  style={{display:"inline-flex",alignItems:"center",gap:"0.35rem",padding:"0.4rem 0.9rem",borderRadius:"100px",border:"1.5px solid var(--border)",background:"white",color:"var(--black)",fontSize:"0.75rem",fontWeight:700,textDecoration:"none"}}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  {l.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {dev.bio && (
          <div style={{background:"white",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.75rem"}}>About</div>
            <p style={{fontSize:"0.92rem",color:"var(--mid)",lineHeight:1.75,fontWeight:300}}>{dev.bio}</p>
          </div>
        )}

        <div style={{background:"white",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.25rem"}}>
          <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.75rem"}}>Skills & Tech Stack</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:"0.45rem"}}>
            {(dev.skills||[]).map(s=><span key={s} className="skill-tag">{s}</span>)}
          </div>
        </div>

        {(dev.past_projects||dev.projects?.length>0) && (
          <div style={{background:"white",border:"1.5px solid var(--rose)",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--rose)",marginBottom:"0.75rem"}}>✦ Past Projects -- Trust signal</div>
            <p style={{fontSize:"0.9rem",color:"var(--mid)",lineHeight:1.75,fontWeight:300}}>{dev.past_projects || dev.projects?.map(p=>p.desc).join(". ")}</p>
          </div>
        )}

        {(dev.industries||[]).length > 0 && (
          <div style={{background:"white",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.75rem"}}>Industries</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:"0.45rem"}}>
              {dev.industries.map(i=><span key={i} style={{padding:"0.35rem 0.85rem",borderRadius:"100px",border:"1px solid var(--border)",fontSize:"0.78rem",color:"var(--mid)"}}>{i}</span>)}
            </div>
          </div>
        )}

        {(dev.personality_tags||[]).length > 0 && (
          <div style={{background:"white",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.75rem"}}>Their vibe</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:"0.45rem"}}>
              {dev.personality_tags.map(t=><span key={t} style={{padding:"0.35rem 0.85rem",borderRadius:"100px",background:"#FFF0F3",border:"1px solid rgba(232,53,90,0.2)",fontSize:"0.78rem",color:"var(--rose)",fontWeight:600}}>{t}</span>)}
            </div>
          </div>
        )}

        {dev.icebreaker && (
          <div style={{background:"var(--black)",border:"none",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"#666",marginBottom:"0.5rem"}}>The last thing I built was...</div>
            <p style={{fontSize:"0.95rem",color:"white",lineHeight:1.7,fontStyle:"italic"}}>"{dev.icebreaker}"</p>
          </div>
        )}
        {[
          {val:dev.icebreaker2, label:"I'm proudest of..."},
          {val:dev.icebreaker3, label:"My dream project is..."},
          {val:dev.icebreaker4, label:"Outside of code, I'm known for..."},
        ].filter(i=>i.val).map((item,idx)=>(
          <div key={idx} style={{background:"var(--black)",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"#666",marginBottom:"0.5rem"}}>{item.label}</div>
            <p style={{fontSize:"0.95rem",color:"white",lineHeight:1.7,fontStyle:"italic"}}>"{item.val}"</p>
          </div>
        ))}
        {(dev.portfolio_images||[]).length > 0 && (
          <div style={{marginBottom:"1.5rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.75rem"}}>Portfolio</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"0.75rem"}}>
              {dev.portfolio_images.map((url,i)=>(
                <div key={i} onClick={()=>setLightboxUrl(url)} style={{borderRadius:"14px",overflow:"hidden",aspectRatio:"4/3",background:"var(--cream)",cursor:"zoom-in",position:"relative"}}>
                  <img src={url} alt={`Portfolio ${i+1}`} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0)",transition:"background 0.2s",display:"flex",alignItems:"center",justifyContent:"center"}}
                    onMouseOver={e=>e.currentTarget.style.background="rgba(0,0,0,0.2)"}
                    onMouseOut={e=>e.currentTarget.style.background="rgba(0,0,0,0)"}>
                    <span style={{opacity:0,fontSize:"1.5rem"}}>🔍</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{display:"flex",gap:"0.75rem",position:"sticky",bottom:"1rem"}}>
          {isOwn
            ? <button className="modal-connect" style={{flex:1,padding:"1rem",background:"var(--black)"}} onClick={()=>{onClose();onNavigate("dashboard");}}>Edit your profile →</button>
            : <button className="modal-connect" style={{flex:1,padding:"1rem"}} onClick={()=>onConnect(dev)}>Connect with {dev.name.split(" ")[0]} →</button>
          }
        </div>
      </div>
      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={()=>setLightboxUrl(null)}/>}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div style={{width:"52px",height:"52px",borderRadius:"14px",background:dev.accent||"var(--rose)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Playfair Display',serif",fontWeight:"700",color:"white",fontSize:"1.1rem",marginBottom:"0.85rem",overflow:"hidden"}}>{avatar}</div>
        <div className="modal-name">{dev.name}</div>
        <div className="modal-meta">{dev.level} · {dev.city} · {dev.available||dev.availability}</div>
        {links[0] && <a href={links[0].url} target="_blank" rel="noopener noreferrer" className="modal-portfolio">{links[0].label} ↗</a>}
        <div className="modal-bio">{dev.bio}</div>
        <div className="modal-skills">{(dev.skills||[]).map(s=><span key={s} className="skill-tag">{s}</span>)}</div>
        {(dev.past_projects||dev.projects?.length>0) && (
          <div className="modal-projects">
            <div className="modal-projects-title">Past projects ✦</div>
            <p style={{fontSize:"0.85rem",color:"var(--mid)",lineHeight:1.6,fontWeight:300}}>{dev.past_projects || dev.projects?.map(p=>p.desc).join(". ")}</p>
          </div>
        )}
        <div style={{marginBottom:"0.75rem"}}>
          <button onClick={()=>setFullPage(true)} style={{background:"none",border:"1.5px solid var(--border)",borderRadius:"100px",padding:"0.45rem 1rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",fontWeight:600,cursor:"pointer",color:"var(--mid)",transition:"all 0.2s"}}
            onMouseOver={e=>e.currentTarget.style.borderColor="var(--rose)"} onMouseOut={e=>e.currentTarget.style.borderColor="var(--border)"}>
            View full profile →
          </button>
        </div>
        {isOwn ? (
          <button className="modal-connect" style={{width:"100%",background:"var(--black)"}} onClick={()=>{onClose();onNavigate("dashboard");}}>Edit your profile →</button>
        ) : (
          <div className="modal-actions">
            <button className="modal-connect" onClick={()=>onConnect(dev)}>Connect with {dev.name.split(" ")[0]}</button>
            <button className={`modal-save-btn ${saved?"is-saved":""}`} onClick={()=>onSave(dev)}>{saved?"♥ Saved":"♡ Save"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectModal({ project, onClose, onConnect, onNavigate, user }) {
  const [fullPage, setFullPage] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  if (!project) return null;
  const isOwn = user && project.user_id && user.id === project.user_id;
  const paidClass = p => p==="Paid"?"paid-paid":p&&p.includes("Equity")?"paid-equity":"paid-volunteer";
  const name = project.name || project.project_name;
  const founder = project.founder || project.founder_name;
  const skills = project.skills || project.skills_needed || [];
  const paid = project.paid || project.paid_type;

  if (fullPage) return (
    <div style={{position:"fixed",inset:0,background:"var(--cream)",zIndex:1000,overflowY:"auto"}}>
      <div style={{position:"sticky",top:0,background:"white",borderBottom:"1px solid var(--border)",zIndex:10,padding:"0 2rem",display:"flex",alignItems:"center",justifyContent:"space-between",height:"60px",boxShadow:"0 1px 12px rgba(0,0,0,0.04)"}}>
        <button onClick={()=>setFullPage(false)} style={{display:"flex",alignItems:"center",gap:"0.4rem",background:"none",border:"none",cursor:"pointer",color:"var(--muted)",fontFamily:"'DM Sans',sans-serif",fontSize:"0.82rem",fontWeight:600,flexShrink:0}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Back
        </button>
        <div style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:"1.05rem"}}>The Match<span style={{color:"var(--rose)",fontStyle:"italic"}}>Deck</span></div>
        {isOwn
          ? <button onClick={()=>{onClose();onNavigate("dashboard");}} style={{fontSize:"0.78rem",fontWeight:700,background:"var(--rose)",color:"white",border:"none",borderRadius:"100px",padding:"0.4rem 1rem",cursor:"pointer",flexShrink:0}}>Edit project</button>
          : <div style={{width:40}}/>
        }
      </div>
      <div style={{maxWidth:"720px",margin:"0 auto",padding:"2rem 1.5rem"}}>
        {/* Cover image */}
        {(project.cover_image_url || project.project_images?.[0]) && (
          <div style={{borderRadius:"20px",overflow:"hidden",marginBottom:"2rem",aspectRatio:"16/9",background:"var(--cream)"}}>
            <img src={project.cover_image_url||project.project_images[0]} alt={name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          </div>
        )}
        <div style={{marginBottom:"2rem"}}>
          <div style={{display:"flex",gap:"0.75rem",marginBottom:"0.75rem",flexWrap:"wrap"}}>
            <span className={`paid-badge ${paidClass(paid)}`} style={{fontSize:"0.8rem",padding:"0.35rem 0.8rem"}}>{paid}</span>
            {project.urgency && <span className={`project-urgency ${project.urgency?.includes("ASAP")?"urgency-asap":"urgency-soon"}`}>{project.urgency}</span>}
          </div>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"2rem",fontWeight:900,marginBottom:"0.5rem"}}>{name}</h1>
          <div style={{color:"var(--muted)",fontSize:"0.88rem",marginBottom:"0.75rem"}}>by {founder} · {project.city} · {project.industry}</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:"0.5rem"}}>
            {[
              {url:project.website_url, label:"Website"},
              {url:project.instagram_url, label:"Instagram"},
              {url:project.linkedin_url, label:"LinkedIn"},
            ].filter(l=>l.url).map(l=>(
              <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer"
                style={{display:"inline-flex",alignItems:"center",gap:"0.35rem",padding:"0.4rem 0.9rem",borderRadius:"100px",border:"1.5px solid var(--border)",background:"white",color:"var(--black)",fontSize:"0.75rem",fontWeight:700,textDecoration:"none"}}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                {l.label}
              </a>
            ))}
          </div>
        </div>

        <div style={{background:"white",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.25rem"}}>
          <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.75rem"}}>About this project</div>
          <p style={{fontSize:"0.92rem",color:"var(--mid)",lineHeight:1.75,fontWeight:300}}>{project.description}</p>
        </div>

        <div style={{background:"white",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.25rem"}}>
          <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.75rem"}}>Skills needed</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:"0.45rem"}}>
            {skills.map(s=><span key={s} className="skill-tag">{s}</span>)}
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"1rem",marginBottom:"1.5rem"}}>
          <div style={{background:"white",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.4rem"}}>Opportunity type</div>
            <div style={{fontWeight:700,fontSize:"0.95rem"}}>{paid}</div>
          </div>
          <div style={{background:"white",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.4rem"}}>Timeline</div>
            <div style={{fontWeight:700,fontSize:"0.95rem"}}>{project.urgency||"--"}</div>
          </div>
          {project.budget_range && <div style={{background:"white",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.4rem"}}>Budget</div>
            <div style={{fontWeight:700,fontSize:"0.95rem"}}>{project.budget_range}</div>
          </div>}
          {project.project_stage && <div style={{background:"white",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.4rem"}}>Stage</div>
            <div style={{fontWeight:700,fontSize:"0.95rem"}}>{project.project_stage}</div>
          </div>}
          {project.work_preference && <div style={{background:"white",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.4rem"}}>Work preference</div>
            <div style={{fontWeight:700,fontSize:"0.95rem"}}>{project.work_preference}</div>
          </div>}
        </div>

        {project.founder_bio && (
          <div style={{background:"white",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.75rem"}}>About the founder</div>
            <p style={{fontSize:"0.92rem",color:"var(--mid)",lineHeight:1.75,fontWeight:300}}>{project.founder_bio}</p>
          </div>
        )}

        {[
          {val:project.founder_prompt1, label:"This project started because..."},
          {val:project.founder_prompt2, label:"The problem I'm obsessed with solving is..."},
          {val:project.founder_prompt3, label:"The kind of dev I vibe best with..."},
          {val:project.founder_prompt4, label:"If this works, in 5 years we'll..."},
        ].filter(i=>i.val).map((item,idx)=>(
          <div key={idx} style={{background:"var(--black)",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"#666",marginBottom:"0.5rem"}}>{item.label}</div>
            <p style={{fontSize:"0.95rem",color:"white",lineHeight:1.7,fontStyle:"italic"}}>"{item.val}"</p>
          </div>
        ))}

        {(project.project_images||[]).length > 0 && (
          <div style={{marginBottom:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.75rem"}}>Project images</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"0.75rem"}}>
              {project.project_images.map((url,i)=>(
                <div key={i} onClick={()=>setLightboxUrl(url)} style={{borderRadius:"14px",overflow:"hidden",aspectRatio:"4/3",background:"var(--cream)",cursor:"zoom-in"}}>
                  <img src={url} alt={`Project ${i+1}`} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                </div>
              ))}
            </div>
          </div>
        )}

        {project.dev_vibe && (
          <div style={{background:"#FFF0F3",border:"1.5px solid var(--rose)",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.25rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--rose)",marginBottom:"0.75rem"}}>The dev they're looking for</div>
            <p style={{fontSize:"0.92rem",color:"var(--mid)",lineHeight:1.75,fontWeight:300}}>{project.dev_vibe}</p>
          </div>
        )}

        {(project.perks||[]).length > 0 && (
          <div style={{background:"white",border:"1px solid var(--border)",borderRadius:"16px",padding:"1.5rem",marginBottom:"1.5rem"}}>
            <div style={{fontSize:"0.7rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.75rem"}}>Perks</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:"0.45rem"}}>
              {project.perks.map(p=><span key={p} style={{padding:"0.35rem 0.85rem",borderRadius:"100px",background:"var(--cream)",border:"1px solid var(--border)",fontSize:"0.78rem",color:"var(--mid)"}}>{p}</span>)}
            </div>
          </div>
        )}

        {isOwn
          ? <button className="modal-connect" style={{width:"100%",padding:"1rem",fontSize:"1rem",background:"var(--black)"}} onClick={()=>{onClose();onNavigate("dashboard");}}>Edit your project →</button>
          : <button className="modal-connect" style={{width:"100%",padding:"1rem",fontSize:"1rem"}} onClick={()=>onConnect(project)}>Apply to this project →</button>
        }
      </div>
      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={()=>setLightboxUrl(null)}/>}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-name">{name}</div>
        <div className="modal-meta">by {founder} · {project.city}</div>
        <div style={{fontSize:"0.78rem",fontWeight:600,color:"var(--muted)",marginBottom:"0.75rem"}}>{project.industry} · {project.urgency}</div>
        <div className="modal-bio">{project.description}</div>
        <div className="modal-skills">{skills.map(s=><span key={s} className="skill-tag">{s}</span>)}</div>
        <div style={{marginBottom:"1rem"}}>
          <span className={`paid-badge ${paidClass(paid)}`} style={{fontSize:"0.8rem",padding:"0.35rem 0.8rem"}}>{paid}</span>
        </div>
        <div style={{marginBottom:"0.75rem"}}>
          <button onClick={()=>setFullPage(true)} style={{background:"none",border:"1.5px solid var(--border)",borderRadius:"100px",padding:"0.45rem 1rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",fontWeight:600,cursor:"pointer",color:"var(--mid)",transition:"all 0.2s"}}
            onMouseOver={e=>e.currentTarget.style.borderColor="var(--rose)"} onMouseOut={e=>e.currentTarget.style.borderColor="var(--border)"}>
            View full project →
          </button>
        </div>
        {isOwn
          ? <button className="modal-connect" style={{width:"100%",background:"var(--black)"}} onClick={()=>{onClose();onNavigate("dashboard");}}>Edit your project →</button>
          : <button className="modal-connect" style={{width:"100%"}} onClick={()=>onConnect(project)}>Apply to this project</button>
        }
      </div>
    </div>
  );
}

// ── VIEWS ─────────────────────────────────────────────────────────────────────

function Hero({ onNavigate }) {
  const [stats, setStats] = useState({ devs: 0, projects: 0, cities: 0 });

  useEffect(() => {
    Promise.all([
      supabase.from("developers").select("city", { count: "exact" }).eq("approved", true),
      supabase.from("projects").select("city", { count: "exact" }).eq("approved", true),
    ]).then(([{ data: devData, count: devCount }, { data: projData, count: projCount }]) => {
      const allCities = [...new Set([
        ...(devData || []).map(d => d.city).filter(Boolean),
        ...(projData || []).map(p => p.city).filter(Boolean),
      ])];
      setStats({ devs: devCount || 0, projects: projCount || 0, cities: allCities.length });
    });
  }, []);

  return (
    <div className="hero">
      <div>
        <div className="hero-eyebrow">For Australian women in tech</div>
        <h1 className="hero-title">Match your idea<br />with the <em>right</em><br />developer.</h1>
        <p className="hero-sub">The Match Deck connects project founders, startups and businesses with talented developers and designers across Australia. Browse yourself or let AI do the matching -- you choose.</p>

        <div className="hero-paths">
          <div className="hero-path" onClick={() => onNavigate("browse-devs")}>
            <div className="hero-path-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </div>
            <div>
              <div className="hero-path-title">Browse yourself</div>
              <div className="hero-path-desc">Filter by skill, city and availability -- find your match directly</div>
            </div>
            <div className="hero-path-arrow">→</div>
          </div>
          <div className="hero-path hero-path-ai" onClick={() => onNavigate("home")}>
            <div className="hero-path-icon hero-path-icon-ai">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            </div>
            <div>
              <div className="hero-path-title">AI matching <span className="hero-path-soon">Coming soon</span></div>
              <div className="hero-path-desc">Describe your project and let our AI find your best-fit devs</div>
            </div>
            <div className="hero-path-arrow">→</div>
          </div>
        </div>

        <div className="hero-actions" style={{marginTop:"1.5rem"}}>
          <button className="btn-primary" onClick={() => onNavigate("submit-project")}>Submit your project</button>
          <button className="btn-secondary" onClick={() => onNavigate("dev-signup")}>Join as a developer</button>
        </div>

        <div className="hero-stats">
          <div><div className="hero-stat-num">{stats.devs > 0 ? stats.devs : "--"}</div><div className="hero-stat-label">Developers</div></div>
          <div><div className="hero-stat-num">{stats.projects > 0 ? stats.projects : "--"}</div><div className="hero-stat-label">Projects</div></div>
          <div><div className="hero-stat-num">{stats.cities > 0 ? stats.cities : "--"}</div><div className="hero-stat-label">Cities</div></div>
        </div>
      </div>

      {/* Hinge-style profile card stack */}
      <div className="hero-cards">
        <div className="hero-card-stack">
          {/* Deck cards behind */}
          <div className="hero-card-back"/>
          <div className="hero-card-back"/>
          {/* Front card -- Hinge profile */}
          <div className="hero-card-front">
            <div className="hero-card-front-img">
              <div className="hero-card-front-initials">ZC</div>
              {/* Gradient overlay */}
              <div style={{position:"absolute",bottom:0,left:0,right:0,height:"60px",background:"linear-gradient(to bottom,transparent,#0D0D0D)"}}/>
              <div className="match-badge">New match ♥</div>
            </div>
            <div className="hero-card-front-body">
              <div className="hero-card-front-name">Zara Chen</div>
              <div className="hero-card-front-meta">Senior Dev · Sydney · Available now</div>
              <div className="hero-card-front-tags">
                <span className="hero-card-front-tag">React</span>
                <span className="hero-card-front-tag">Node.js</span>
                <span className="hero-card-front-tag">TypeScript</span>
                <span className="hero-card-front-tag">UI/UX Design</span>
              </div>
              <div className="hero-card-front-prompt">
                <div className="hero-card-front-prompt-label">The last thing I built was...</div>
                <div className="hero-card-front-prompt-text">"A fintech dashboard that helped 3,000 Australians track their super contributions."</div>
              </div>
              <div className="hero-card-actions">
                <button className="hero-card-btn hero-card-btn-pass">Pass</button>
                <button className="hero-card-btn hero-card-btn-connect">Connect ♥</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { num: "01", title: "Submit your project", desc: "Tell us what you're building, the skills you need, your timeline, and whether it's paid or equity." },
    { num: "02", title: "Get AI-matched", desc: "Our engine analyses your project and surfaces the developers most aligned with your vision." },
    { num: "03", title: "Connect & build", desc: "Browse your matches, view profiles, and reach out directly to the developer you want." },
  ];
  return (
    <div className="hiw">
      <div className="hiw-inner">
        <div className="hiw-label">The process</div>
        <div className="hiw-title">How The Match<em>Deck</em> works</div>
        <div className="hiw-sub">Three steps from idea to team.</div>
        <div className="hiw-grid">
          {steps.map((s, i) => (
            <>
              <div key={s.num} className="hiw-step">
                <div className="hiw-num">{s.num}</div>
                <div className="hiw-step-title">{s.title}</div>
                <div className="hiw-step-desc">{s.desc}</div>
              </div>
              {i < 2 && <div key={`arrow${i}`} className="hiw-arrow">→</div>}
            </>
          ))}
        </div>
      </div>
    </div>
  );
}

function AIMatch() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleWaitlist = async () => {
    if (!email) return;
    setLoading(true);
    await supabase.from("waitlist").insert([{ email }]);
    setSubmitted(true);
    setLoading(false);
  };

  return (
    <div className="ai-section">
      <div className="ai-inner">
        <div className="ai-coming-soon">

          {/* Left -- text */}
          <div className="ai-cs-left">
            <div className="ai-label">✦ Coming soon</div>
            <div className="ai-title">AI-powered matching<br />is on its <em>way.</em></div>
            <div className="ai-sub" style={{marginTop:"0.75rem", maxWidth:"420px", lineHeight:1.75}}>
              Describe your project in plain English and our AI will surface the three best-fit developers from the deck -- matched on skills, city, availability and vibe.
            </div>

            {/* Feature preview pills */}
            <div className="ai-cs-features">
              <div className="ai-cs-feature">
                <div className="ai-cs-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                </div>
                <div>
                  <div className="ai-cs-feature-title">Smart skill matching</div>
                  <div className="ai-cs-feature-desc">Matches on your exact tech stack needs</div>
                </div>
              </div>
              <div className="ai-cs-feature">
                <div className="ai-cs-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                </div>
                <div>
                  <div className="ai-cs-feature-title">3 curated matches</div>
                  <div className="ai-cs-feature-desc">Ranked by fit with a reason for each</div>
                </div>
              </div>
              <div className="ai-cs-feature">
                <div className="ai-cs-feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                </div>
                <div>
                  <div className="ai-cs-feature-title">Instant results</div>
                  <div className="ai-cs-feature-desc">No waiting, no back-and-forth</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right -- notify me */}
          <div className="ai-cs-right">
            <div className="ai-cs-card">
              <div className="ai-cs-card-label">Get early access</div>
              <div className="ai-cs-card-title">Be first to try<br />AI matching</div>
              <div className="ai-cs-card-sub">Drop your email and we'll notify you the moment it's live.</div>

              {submitted ? (
                <div className="ai-cs-success">
                  <div style={{fontSize:"1.5rem", fontFamily:"'Playfair Display',serif", fontWeight:900, color:"var(--rose)", marginBottom:"0.5rem"}}>✦</div>
                  <div style={{fontWeight:600, marginBottom:"0.25rem"}}>You're on the list!</div>
                  <div style={{fontSize:"0.82rem", color:"#888"}}>We'll email you at <strong style={{color:"#ccc"}}>{email}</strong> when AI matching launches.</div>
                </div>
              ) : (
                <>
                  <input
                    className="ai-cs-input"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && email && setSubmitted(true)}
                  />
                  <button
                    className="ai-cs-btn"
                    onClick={handleWaitlist}
                    disabled={!email || loading}
                  >
                    {loading ? "Saving..." : "Notify me when it's live"}
                  </button>
                </>
              )}

              {/* Mock preview */}
              <div className="ai-cs-preview-label">Preview</div>
              <div className="ai-cs-preview">
                <div className="ai-cs-preview-card">
                  <div className="ai-cs-preview-score">94% match</div>
                  <div className="ai-cs-preview-avatar" style={{background:"#E8355A"}}>ZC</div>
                  <div className="ai-cs-preview-name">Zara Chen</div>
                  <div className="ai-cs-preview-meta">Senior · Sydney</div>
                  <div className="ai-cs-preview-reason">"Perfect React & Node.js skill match for your project scope."</div>
                  <div style={{display:"flex", gap:"0.3rem", flexWrap:"wrap"}}>
                    {["React","Node.js","TypeScript"].map(s => <span key={s} style={{fontSize:"0.62rem",fontWeight:600,padding:"0.18rem 0.5rem",borderRadius:"100px",background:"#2a2a2a",color:"#888"}}>{s}</span>)}
                  </div>
                </div>
                <div className="ai-cs-preview-blur" />
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

function BrowseDevs({ saved, onSave, searchQuery, user, onConnect, onNavigate }) {
  const [activeLevel, setActiveLevel] = useState("All");
  const [activeCity, setActiveCity] = useState("All");
  const [selectedDev, setSelectedDev] = useState(null);
  const [devs, setDevs] = useState(MOCK_DEVS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from("developers").select("*").eq("approved", true).order("created_at", { ascending: false })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setDevs(data.map(d => ({
            ...d,
            skills: d.skills || [],
            available: d.availability,
            portfolio: d.portfolio_url,
            accent: d.accent_colour || "#E8355A",
          })));
        }
        setLoading(false);
      });
  }, []);

  const filtered = devs.filter(d => {
    const levelOk = activeLevel === "All" || d.level === activeLevel;
    const cityOk = activeCity === "All" || d.city === activeCity;
    const q = searchQuery.toLowerCase();
    const qOk = !q || d.name.toLowerCase().includes(q) || (d.skills || []).some(s => s.toLowerCase().includes(q)) || (d.city||"").toLowerCase().includes(q) || (d.bio||"").toLowerCase().includes(q);
    return levelOk && cityOk && qOk;
  });

  return (
    <div className="section">
      <div className="section-label">Browse Talent</div>
      <div className="section-title">Find your <em style={{ fontFamily: "'Playfair Display',serif", fontStyle: "italic", color: "var(--rose)" }}>perfect match</em></div>
      <div className="section-sub">Talented developers and designers across Australia, ready to build with you.</div>

      {loading ? (
        <div className="dev-grid" style={{marginTop:"2rem"}}>
          {[1,2,3,4,5,6].map(i => <SkeletonDevCard key={i} />)}
        </div>
      ) : (
      <>
      <div className="filters">
        <span className="filter-label">Level</span>
        {["All","Junior","Mid-level","Senior"].map(l => <button key={l} className={`filter-chip ${activeLevel===l?"active":""}`} onClick={()=>setActiveLevel(l)}>{l}</button>)}
        <span className="filter-label" style={{marginLeft:"0.75rem"}}>City</span>
        {["All","Sydney","Melbourne","Brisbane","Gold Coast","Perth","Adelaide","Canberra","Remote / Online"].map(c => <button key={c} className={`filter-chip ${activeCity===c?"active":""}`} onClick={()=>setActiveCity(c)}>{c}</button>)}
      </div>
      <div className="dev-grid">
        {filtered.map(dev => (
          <div key={dev.id} className="dev-card" style={{"--accent":dev.accent,"--accent-bg":dev.accent+"18"}} onClick={()=>setSelectedDev(dev)}>
            <div className="dev-card-top">
              <div className="dev-avatar" style={{overflow:"hidden",background:dev.avatar_url?"transparent":undefined}}>
                {dev.avatar_url
                  ? <img src={dev.avatar_url} alt={dev.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  : dev.name.split(" ").map(n=>n[0]).join("")}
              </div>
              <div style={{display:"flex",gap:"0.4rem",alignItems:"center",flexWrap:"wrap",justifyContent:"flex-end"}}>
                {dev.created_at && (new Date() - new Date(dev.created_at)) < 7*24*60*60*1000 && (
                  <span style={{fontSize:"0.6rem",fontWeight:700,letterSpacing:"0.07em",textTransform:"uppercase",background:"#E8355A",color:"white",padding:"0.2rem 0.55rem",borderRadius:"100px"}}>New</span>
                )}
                <span className={`dev-badge badge-${dev.level?.toLowerCase().split("-")[0]}`}>{dev.level}</span>
              </div>
            </div>
            <div className="dev-name">{dev.name}</div>
            <div className="dev-meta">{dev.city}</div>
            <div className="dev-bio">{dev.bio}</div>
            {dev.icebreaker && (
              <div style={{background:"var(--black)",borderRadius:"10px",padding:"0.65rem 0.85rem",margin:"0.5rem 0 0.75rem",fontSize:"0.75rem",color:"#aaa",fontStyle:"italic",lineHeight:1.5}}>
                <span style={{fontSize:"0.6rem",fontWeight:700,letterSpacing:"0.07em",textTransform:"uppercase",color:"#555",display:"block",marginBottom:"0.25rem"}}>Last built...</span>
                "{dev.icebreaker.length > 80 ? dev.icebreaker.slice(0,80)+"..." : dev.icebreaker}"
              </div>
            )}
            <div className="dev-skills">{(dev.skills||[]).slice(0,5).map(s=><span key={s} className="skill-tag">{s}</span>)}{(dev.skills||[]).length>5 && <span className="skill-tag">+{dev.skills.length-5}</span>}</div>
            <div className="dev-card-footer">
              <span className="dev-avail">{dev.available||dev.availability}</span>
              <div style={{display:"flex",gap:"0.5rem",alignItems:"center"}}>
                {(!user || user.id !== dev.user_id) && <button className={`save-heart ${saved.some(x=>x.id===dev.id)?"saved":""}`} onClick={e=>{e.stopPropagation();onSave(dev);}}>{saved.some(x=>x.id===dev.id)?"♥":"♡"}</button>}
                <button className="connect-btn" onClick={e=>{e.stopPropagation();setSelectedDev(dev);}}>{user && user.id===dev.user_id?"Edit →":"View →"}</button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length===0 && (
          <div style={{textAlign:"center",padding:"4rem 2rem",color:"var(--muted)"}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.3rem",fontWeight:700,color:"var(--black)",marginBottom:"0.75rem"}}>Be the first in the deck.</div>
            <div style={{fontSize:"0.9rem",lineHeight:1.7,marginBottom:"1.5rem"}}>No developer profiles yet -- but that's about to change.<br/>Are you a developer? Join now and be first to be matched.</div>
            <button className="btn-primary" onClick={()=>onNavigate("dev-signup")}>Join as a developer →</button>
          </div>
        )}
      </div>
      {selectedDev && <DevModal dev={selectedDev} saved={saved.some(x=>x.id===selectedDev.id)} onSave={onSave} onClose={()=>setSelectedDev(null)} onConnect={onConnect} onNavigate={onNavigate} user={user}/>}
      </>
      )}
    </div>
  );
}

function BrowseProjects({ searchQuery, user, onConnect, onNavigate }) {
  const [activeIndustry, setActiveIndustry] = useState("All");
  const [activePaid, setActivePaid] = useState("All");
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState(MOCK_PROJECTS);

  useState(() => {
    supabase.from("projects").select("*").eq("approved", true).order("created_at", { ascending: false })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setProjects(data.map(p => ({
            ...p,
            name: p.project_name,
            founder: p.founder_name,
            skills: p.skills_needed || [],
            paid: p.paid_type,
            accent: "#E8355A",
          })));
        }
      });
  }, []);

  const filtered = projects.filter(p => {
    const iOk = activeIndustry === "All" || p.industry === activeIndustry;
    const pOk = activePaid === "All" || (p.paid || p.paid_type) === activePaid;
    const q = searchQuery.toLowerCase();
    const qOk = !q || (p.name||p.project_name||"").toLowerCase().includes(q) || (p.description||"").toLowerCase().includes(q) || (p.skills||[]).some(s=>s.toLowerCase().includes(q)) || (p.city||"").toLowerCase().includes(q);
    return iOk && pOk && qOk;
  });

  const urgencyClass = u => u && u.includes("ASAP") ? "urgency-asap" : u && u.includes("exploring") ? "urgency-exploring" : "urgency-soon";
  const paidClass = p => p === "Paid" ? "paid-paid" : p && p.includes("Equity") ? "paid-equity" : "paid-volunteer";

  return (
    <div className="section">
      <div className="section-label">Open Projects</div>
      <div className="section-title">Projects looking for <em style={{fontFamily:"'Playfair Display',serif",fontStyle:"italic",color:"var(--rose)"}}>you</em></div>
      <div className="section-sub">Real founders. Real ideas. Ready to build.</div>
      <div className="filters">
        <span className="filter-label">Industry</span>
        {["All","Health & Wellness","Fashion & Beauty","EdTech","Food & Hospitality","Legal Tech"].map(i=><button key={i} className={`filter-chip ${activeIndustry===i?"active":""}`} onClick={()=>setActiveIndustry(i)}>{i}</button>)}
        <span className="filter-label" style={{marginLeft:"0.75rem"}}>Type</span>
        {["All","Paid","Equity / Revenue share"].map(p=><button key={p} className={`filter-chip ${activePaid===p?"active":""}`} onClick={()=>setActivePaid(p)}>{p}</button>)}
      </div>
      <div className="project-grid">
        {filtered.map(p => (
          <div key={p.id} className="project-card" style={{"--p-accent":p.accent,"--p-accent-bg":p.accent+"18"}} onClick={()=>setSelectedProject(p)}>
            {p.cover_image_url
              ? <img src={p.cover_image_url} alt={p.project_name} className="project-card-cover"/>
              : (p.project_images?.[0]
                ? <img src={p.project_images[0]} alt={p.project_name} className="project-card-cover"/>
                : null)
            }
            <div className="project-card-body">
              <div className="project-card-top">
                <div style={{display:"flex",alignItems:"center",gap:"0.75rem"}}>
                  <div className="project-emoji-wrap">{(p.name||p.project_name||"?").split(" ").map(n=>n[0]).join("").slice(0,2)}</div>
                  <div>
                    <div className="project-name">{p.name||p.project_name}</div>
                    <div className="project-meta">by {p.founder||p.founder_name} · {p.city}</div>
                  </div>
                </div>
                <span className={`project-urgency ${urgencyClass(p.urgency)}`}>{p.urgency}</span>
              </div>
              <div className="project-desc">{p.description}</div>
              <div className="dev-skills">{(p.skills||p.skills_needed||[]).slice(0,4).map(s=><span key={s} className="skill-tag">{s}</span>)}{(p.skills||p.skills_needed||[]).length>4&&<span className="skill-tag">+{(p.skills||p.skills_needed||[]).length-4}</span>}</div>
              <div className="project-card-footer">
                <span className={`paid-badge ${paidClass(p.paid||p.paid_type)}`}>{p.paid||p.paid_type}</span>
                <button className="apply-btn" onClick={e=>{e.stopPropagation();setSelectedProject(p);}}>
                  {user && p.user_id && user.id===p.user_id ? "Edit →" : "View →"}
                </button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length===0 && (
          <div style={{textAlign:"center",padding:"4rem 2rem",color:"var(--muted)"}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.3rem",fontWeight:700,color:"var(--black)",marginBottom:"0.75rem"}}>No projects listed yet.</div>
            <div style={{fontSize:"0.9rem",lineHeight:1.7,marginBottom:"1.5rem"}}>Got an idea you want to build? Submit your project and we'll match you<br/>with the right developer from our community.</div>
            <button className="btn-primary" onClick={()=>window.scrollTo({top:0})}>Submit a project →</button>
          </div>
        )}
      </div>
      {selectedProject && <ProjectModal project={selectedProject} onClose={()=>setSelectedProject(null)} onConnect={onConnect} onNavigate={onNavigate} user={user}/>}
    </div>
  );
}

function DevSignup({ user, onNeedAuth }) {
  const [form, setForm] = useState({
    name: "", email: user?.email || "", city:"", level:"",
    portfolioUrl:"", linkedinUrl:"", githubUrl:"", instagramUrl:"", otherUrl:"",
    bio:"", skills:[], industries:[], available:"", pastProjects:"", workPreference:""
  });
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (field, val) => setForm(f => ({ ...f, [field]: f[field].includes(val) ? f[field].filter(x=>x!==val) : [...f[field],val] }));

  const handleSubmit = async () => {
    if (!form.name || !form.email) { setError("Please fill in your name and email."); return; }
    if (!user) { onNeedAuth(); return; }
    setLoading(true); setError(null);
    // Check for existing profile
    const { data: existing } = await supabase.from("developers").select("id").eq("user_id", user.id).single();
    if (existing) {
      setError("You already have a developer profile. Go to your dashboard to view or edit it.");
      setLoading(false); return;
    }
    const { error: dbError } = await supabase.from("developers").insert([{
      name: form.name,
      email: form.email,
      city: form.city,
      level: form.level,
      portfolio_url: form.portfolioUrl,
      linkedin_url: form.linkedinUrl,
      github_url: form.githubUrl,
      instagram_url: form.instagramUrl,
      other_url: form.otherUrl,
      bio: form.bio,
      skills: form.skills,
      industries: form.industries,
      availability: form.available,
      past_projects: form.pastProjects,
      work_preference: form.workPreference,
      personality_tags: form.personalityTags || [],
      icebreaker: form.icebreaker1 || "",
      icebreaker2: form.icebreaker2 || "",
      icebreaker3: form.icebreaker3 || "",
      icebreaker4: form.icebreaker4 || "",
      portfolio_images: form.portfolioImages || [],
      avatar_url: avatarUrl,
      user_id: user.id,
      approved: true,
    }]);
    if (dbError) {
      setError(dbError.message.includes("unique") ? "A profile with this email already exists." : dbError.message);
      setLoading(false); return;
    }
    // Send emails
    const welcome = emailTemplates.welcome(form.name, "developer");
    await sendEmail({ to: form.email, ...welcome });
    const admin = emailTemplates.newDevProfile(form.name, form.email);
    await sendEmail({ to: "hello@thematchdeck.com", ...admin });

    setSubmitted(true); setLoading(false);
  };

  const resetForm = () => {
    setSubmitted(false);
    setForm({ name:"", email:user?.email||"", city:"", level:"", portfolioUrl:"", linkedinUrl:"", githubUrl:"", instagramUrl:"", otherUrl:"", bio:"", skills:[], industries:[], available:"", pastProjects:"", workPreference:"" });
    setAvatarUrl(null);
  };

  if (submitted) return (
    <div className="section"><div className="form-card"><div className="success-state">
      <div className="success-icon">✦</div>
      <div className="success-title">You're in the deck.</div>
      <div className="success-sub">Welcome, {form.name.split(" ")[0]}! Your profile is live. Check your inbox at <strong>{form.email}</strong>.</div>
      <button className="btn-primary" onClick={resetForm}>Submit another profile</button>
    </div></div></div>
  );

  return (
    <div className="section">
      <div className="section-label">Join as a Developer</div>
      <div className="section-title">Deal yourself <em style={{fontFamily:"'Playfair Display',serif",fontStyle:"italic",color:"var(--rose)"}}>into the deck</em></div>
      <div className="section-sub">Create your profile and start connecting with founders who need your skills. One-off work, part-time projects, full builds -- you choose.</div>
      {!user && (
        <div style={{background:"#FFF0F3",border:"1.5px solid var(--rose)",borderRadius:"14px",padding:"1rem 1.25rem",marginBottom:"1.5rem",display:"flex",alignItems:"center",gap:"1rem",maxWidth:"700px",margin:"0 auto 1.5rem"}}>
          <div style={{flex:1,fontSize:"0.88rem",color:"var(--mid)"}}>You'll need an account to submit your profile. <strong>Create one now</strong> -- it only takes a minute.</div>
          <button className="btn-primary" style={{whiteSpace:"nowrap",padding:"0.6rem 1.2rem",fontSize:"0.82rem"}} onClick={onNeedAuth}>Sign up free →</button>
        </div>
      )}
      <div className="form-card">
        {error && <div style={{color:"var(--rose)",fontSize:"0.85rem",marginBottom:"1rem",padding:"0.75rem",background:"#FFF0F3",borderRadius:"10px"}}>{error}</div>}

        {user && (
          <div className="form-group full" style={{marginBottom:"1.25rem"}}>
            <label className="form-label">Profile Photo</label>
            <AvatarUpload userId={user.id} currentUrl={avatarUrl} onUpload={url=>setAvatarUrl(url)} />
          </div>
        )}

        <div className="form-grid">
          <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" placeholder="Zara Chen" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></div>
          <div className="form-group"><label className="form-label">Email *</label><input className="form-input" type="email" placeholder="zara@gmail.com" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} /></div>
          <div className="form-group"><label className="form-label">City</label><select className="form-select" value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))}><option value="">Select city...</option>{CITIES.map(c=><option key={c}>{c}</option>)}</select></div>
          <div className="form-group"><label className="form-label">Experience Level</label><select className="form-select" value={form.level} onChange={e=>setForm(f=>({...f,level:e.target.value}))}><option value="">Select...</option><option>Junior</option><option>Mid-level</option><option>Senior</option></select></div>
          <div className="form-group full"><label className="form-label">About You</label><textarea className="form-textarea" placeholder="Tell founders who you are, what you love building, and what makes you great to work with..." value={form.bio} onChange={e=>setForm(f=>({...f,bio:e.target.value}))} /></div>

          {/* Links section */}
          <div className="form-group full">
            <label className="form-label">Your Links</label>
            <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
              <div style={{position:"relative"}}>
                <span style={{position:"absolute",left:"0.9rem",top:"50%",transform:"translateY(-50%)",fontSize:"0.75rem",fontWeight:700,color:"var(--muted)",width:"80px"}}>Portfolio</span>
                <input className="form-input" style={{paddingLeft:"5.5rem"}} placeholder="https://yoursite.com" value={form.portfolioUrl} onChange={e=>setForm(f=>({...f,portfolioUrl:e.target.value}))} />
              </div>
              <div style={{position:"relative"}}>
                <span style={{position:"absolute",left:"0.9rem",top:"50%",transform:"translateY(-50%)",fontSize:"0.75rem",fontWeight:700,color:"var(--muted)",width:"80px"}}>LinkedIn</span>
                <input className="form-input" style={{paddingLeft:"5.5rem"}} placeholder="https://linkedin.com/in/you" value={form.linkedinUrl} onChange={e=>setForm(f=>({...f,linkedinUrl:e.target.value}))} />
              </div>
              <div style={{position:"relative"}}>
                <span style={{position:"absolute",left:"0.9rem",top:"50%",transform:"translateY(-50%)",fontSize:"0.75rem",fontWeight:700,color:"var(--muted)",width:"80px"}}>GitHub</span>
                <input className="form-input" style={{paddingLeft:"5.5rem"}} placeholder="https://github.com/you" value={form.githubUrl} onChange={e=>setForm(f=>({...f,githubUrl:e.target.value}))} />
              </div>
              <div style={{position:"relative"}}>
                <span style={{position:"absolute",left:"0.9rem",top:"50%",transform:"translateY(-50%)",fontSize:"0.75rem",fontWeight:700,color:"var(--muted)",width:"80px"}}>Instagram</span>
                <input className="form-input" style={{paddingLeft:"5.5rem"}} placeholder="https://instagram.com/you" value={form.instagramUrl} onChange={e=>setForm(f=>({...f,instagramUrl:e.target.value}))} />
              </div>
              <div style={{position:"relative"}}>
                <span style={{position:"absolute",left:"0.9rem",top:"50%",transform:"translateY(-50%)",fontSize:"0.75rem",fontWeight:700,color:"var(--muted)",width:"80px"}}>Other</span>
                <input className="form-input" style={{paddingLeft:"5.5rem"}} placeholder="Any other link" value={form.otherUrl} onChange={e=>setForm(f=>({...f,otherUrl:e.target.value}))} />
              </div>
            </div>
          </div>

          <div className="form-group full"><label className="form-label">Skills & Tech Stack</label><GroupedSkillPicker selected={form.skills} onToggle={s=>toggle("skills",s)} /></div>
          <div className="form-group full"><label className="form-label">Industries You Love</label><div className="skill-picker">{INDUSTRIES.map(i=><button key={i} className={`skill-pick-btn ${form.industries.includes(i)?"selected":""}`} onClick={()=>toggle("industries",i)}>{i}</button>)}</div></div>
          <div className="form-group full"><label className="form-label">Availability</label><select className="form-select" value={form.available} onChange={e=>setForm(f=>({...f,available:e.target.value}))}><option value="">Select...</option><option>Available now</option><option>Available in 2 weeks</option><option>Available in 1 month</option><option>Available for part-time only</option></select></div>
          <div className="form-group full"><label className="form-label">Work Preference</label><select className="form-select" value={form.workPreference} onChange={e=>setForm(f=>({...f,workPreference:e.target.value}))}><option value="">Select...</option><option>Remote only</option><option>In-person only</option><option>Hybrid -- remote & in-person</option><option>No preference</option></select></div>
          <div className="form-group full">
            <label className="form-label">Past Projects ✦ Most important for trust</label>
            <textarea className="form-textarea" style={{minHeight:"110px",borderColor:"var(--rose)"}} placeholder="Tell founders what you've built. e.g. 'Built a Shopify store for a Brisbane fashion label -- doubled their online sales in 3 months.' Be specific about what you built and the outcome." value={form.pastProjects} onChange={e=>setForm(f=>({...f,pastProjects:e.target.value}))} />
            <span style={{fontSize:"0.72rem",color:"var(--rose)",fontWeight:600}}>✦ This is the #1 thing founders look for -- be specific about what you built and the outcome.</span>
          </div>

          {/* Personality tags -- Hinge style */}
          <div className="form-group full">
            <label className="form-label">Your vibe -- pick all that apply</label>
            <div style={{fontSize:"0.75rem",color:"var(--muted)",marginBottom:"0.6rem"}}>These help founders understand how you work</div>
            <div className="skill-picker">
              {["I work best async","I love early-stage chaos","I'm a finisher, not just a starter","I care about design details","I move fast and iterate","I love a good brief","I prefer long-term projects","I'm great at explaining tech to non-techies","I'm a solo operator","I love collaborating","I build for impact","I'm obsessed with clean code"].map(tag=>(
                <button key={tag} className={`skill-pick-btn ${(form.personalityTags||[]).includes(tag)?"selected":""}`}
                  onClick={()=>setForm(f=>({...f,personalityTags:(f.personalityTags||[]).includes(tag)?(f.personalityTags||[]).filter(x=>x!==tag):[...(f.personalityTags||[]),tag]}))}>
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Icebreakers -- Hinge style, pick prompt + answer */}
          <div className="form-group full">
            <label className="form-label">Icebreakers ✨</label>
            <div style={{fontSize:"0.75rem",color:"var(--muted)",marginBottom:"0.75rem"}}>Pick up to 3 prompts and answer them -- these make your profile stand out</div>
            {[
              { key:"icebreaker1", prompt:"The last thing I built was...", placeholder:"e.g. A mobile app that helps hospo workers find last-minute shifts..." },
              { key:"icebreaker2", prompt:"I'm proudest of...", placeholder:"e.g. Rebuilding my client's entire checkout flow and cutting drop-off by 40%..." },
              { key:"icebreaker3", prompt:"My dream project is...", placeholder:"e.g. An app that helps women in regional areas access mental health support..." },
              { key:"icebreaker4", prompt:"Outside of code, I'm known for...", placeholder:"e.g. Being the person who brings homemade food to every team meeting..." },
            ].map(({key, prompt, placeholder})=>(
              <div key={key} style={{background:"var(--cream)",border:"1.5px solid var(--border)",borderRadius:"14px",padding:"1rem 1.1rem",marginBottom:"0.75rem",transition:"border-color 0.2s"}}
                onFocus={e=>e.currentTarget.style.borderColor="var(--rose)"} onBlur={e=>e.currentTarget.style.borderColor="var(--border)"}>
                <div style={{fontSize:"0.82rem",fontWeight:700,color:"var(--black)",marginBottom:"0.5rem"}}>{prompt}</div>
                <textarea className="form-textarea" style={{minHeight:"65px",background:"white",border:"1px solid var(--border)"}}
                  placeholder={placeholder}
                  value={form[key]||""}
                  onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}/>
              </div>
            ))}
          </div>

          {/* Portfolio images */}
          {user && (
            <div className="form-group full">
              <label className="form-label">Portfolio Images</label>
              <div style={{fontSize:"0.75rem",color:"var(--muted)",marginBottom:"0.75rem"}}>Add up to 4 screenshots of your work -- projects, designs, dashboards, anything you're proud of</div>
              <PortfolioImageUpload userId={user.id} images={form.portfolioImages||[]} onUpdate={imgs=>setForm(f=>({...f,portfolioImages:imgs}))}/>
            </div>
          )}

        </div>
        <button className="form-submit" onClick={handleSubmit} disabled={loading}>{loading ? "Submitting..." : "Join the deck"}</button>
      </div>
    </div>
  );
}

// ── SKILL HELPER WIZARD ──────────────────────────────────────────────────────
const SKILL_QUESTIONS = [
  {
    q: "What do you need built?",
    options: [
      { label: "A website", skills: ["HTML / CSS", "JavaScript"] },
      { label: "A mobile app", skills: ["React Native", "iOS", "Android"] },
      { label: "A web app (with accounts, data etc)", skills: ["React", "Node.js", "PostgreSQL"] },
      { label: "An online store", skills: ["Shopify", "WordPress"] },
      { label: "Something with data or AI", skills: ["Python", "Data Science", "Machine Learning / AI"] },
      { label: "Not sure yet", skills: [] },
    ]
  },
  {
    q: "Does it need user accounts / logins?",
    options: [
      { label: "Yes", skills: ["Node.js", "REST APIs", "PostgreSQL"] },
      { label: "No", skills: [] },
      { label: "Not sure", skills: [] },
    ]
  },
  {
    q: "Does it need to look custom and designed?",
    options: [
      { label: "Yes -- I need a designer too", skills: ["UI/UX Design"] },
      { label: "Just needs to look clean and professional", skills: ["HTML / CSS"] },
      { label: "Design isn't the priority right now", skills: [] },
    ]
  },
  {
    q: "Will it need to connect to other tools? (payments, emails, maps etc)",
    options: [
      { label: "Yes -- payments like Stripe", skills: ["Node.js", "REST APIs"] },
      { label: "Yes -- third party APIs", skills: ["REST APIs"] },
      { label: "No / not sure", skills: [] },
    ]
  },
  {
    q: "Where should it live?",
    options: [
      { label: "In a browser (website)", skills: ["HTML / CSS", "JavaScript", "React"] },
      { label: "On people's phones", skills: ["React Native", "iOS", "Android"] },
      { label: "Both", skills: ["React", "React Native"] },
      { label: "Not sure", skills: [] },
    ]
  },
];

function SkillHelperWizard({ onDone, onSkip }) {
  const [step, setStep] = useState(0);
  const [suggestedSkills, setSuggestedSkills] = useState([]);

  const handleAnswer = (skills) => {
    const merged = [...new Set([...suggestedSkills, ...skills])];
    setSuggestedSkills(merged);
    if (step < SKILL_QUESTIONS.length - 1) {
      setStep(s => s + 1);
    } else {
      onDone(merged);
    }
  };

  const current = SKILL_QUESTIONS[step];

  return (
    <div className="skill-wizard">
      <div className="skill-wizard-header">
        <div className="skill-wizard-step">{step + 1} of {SKILL_QUESTIONS.length}</div>
        <div className="skill-wizard-progress">
          <div className="skill-wizard-bar" style={{width: `${((step + 1) / SKILL_QUESTIONS.length) * 100}%`}} />
        </div>
      </div>
      <div className="skill-wizard-q">{current.q}</div>
      <div className="skill-wizard-options">
        {current.options.map((opt, i) => (
          <button key={i} className="skill-wizard-opt" onClick={() => handleAnswer(opt.skills)}>
            {opt.label}
          </button>
        ))}
      </div>
      <button className="skill-wizard-skip" onClick={onSkip}>Skip -- I'll choose skills myself</button>
    </div>
  );
}

function SubmitProject({ user, onNeedAuth }) {
  const [form, setForm] = useState({ name:"", email: user?.email || "", projectName:"", industry:"", description:"", skills:[], urgency:"", paid:"", city:"", workPreference:"", budgetRange:"", projectStage:"", founderBio:"", devVibe:"", perks:[], websiteUrl:"", instagramUrl:"", linkedinUrl:"" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [skillMode, setSkillMode] = useState("choose");

  const toggle = s => setForm(f=>({...f,skills:f.skills.includes(s)?f.skills.filter(x=>x!==s):[...f.skills,s]}));

  const handleWizardDone = (skills) => {
    setForm(f => ({ ...f, skills: [...new Set([...f.skills, ...skills])] }));
    setSkillMode("choose");
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.projectName) { setError("Please fill in your name, email and project name."); return; }
    if (!user) { onNeedAuth(); return; }
    setLoading(true); setError(null);
    const { error: dbError } = await supabase.from("projects").insert([{
      founder_name: form.name,
      founder_email: form.email,
      city: form.city,
      project_name: form.projectName,
      industry: form.industry,
      description: form.description,
      skills_needed: form.skills,
      urgency: form.urgency,
      paid_type: form.paid,
      work_preference: form.workPreference,
      budget_range: form.budgetRange || "",
      project_stage: form.projectStage || "",
      founder_bio: form.founderBio || "",
      dev_vibe: form.devVibe || "",
      perks: form.perks || [],
      website_url: form.websiteUrl || "",
      instagram_url: form.instagramUrl || "",
      linkedin_url: form.linkedinUrl || "",
      founder_prompt1: form.founderPrompt1 || "",
      founder_prompt2: form.founderPrompt2 || "",
      founder_prompt3: form.founderPrompt3 || "",
      founder_prompt4: form.founderPrompt4 || "",
      project_images: form.projectImages || [],
      user_id: user?.id || null,
      approved: true,
    }]);
    if (dbError) { setError(dbError.message); setLoading(false); return; }
    // Send emails
    const welcome = emailTemplates.welcome(form.name, "owner");
    await sendEmail({ to: form.email, ...welcome });
    const admin = emailTemplates.newProject(form.projectName, form.name, form.email);
    await sendEmail({ to: "hello@thematchdeck.com", ...admin });
    setSubmitted(true); setLoading(false);
  };

  if (submitted) return (
    <div className="section"><div className="form-card"><div className="success-state">
      <div className="success-icon">✦</div>
      <div className="success-title">Your project is in the deck.</div>
      <div className="success-sub">We'll start matching you with the right developer shortly. Check your inbox at <strong>{form.email}</strong>.</div>
      <button className="btn-primary" onClick={()=>{ setSubmitted(false); setForm({ name:"",email:user?.email||"",projectName:"",industry:"",description:"",skills:[],urgency:"",paid:"",city:"",workPreference:"",budgetRange:"",projectStage:"",founderBio:"",devVibe:"",perks:[],websiteUrl:"",instagramUrl:"",linkedinUrl:"" }); setSkillMode("choose"); }}>Submit another project</button>
    </div></div></div>
  );

  return (
    <div className="section">
      <div className="section-label">Submit a Project</div>
      <div className="section-title">Deal us <em style={{fontFamily:"'Playfair Display',serif",fontStyle:"italic",color:"var(--rose)"}}>your cards</em></div>
      <div className="section-sub">Tell us about your project and we'll find your perfect match. One-off task, part-time help, or a full build -- any size welcome.</div>
      {!user && (
        <div style={{background:"#FFF0F3",border:"1.5px solid var(--rose)",borderRadius:"14px",padding:"1rem 1.25rem",marginBottom:"1.5rem",display:"flex",alignItems:"center",gap:"1rem",maxWidth:"700px",margin:"0 auto 1.5rem"}}>
          <div style={{flex:1,fontSize:"0.88rem",color:"var(--mid)"}}>You'll need an account to submit a project. <strong>Create one now</strong> -- free and takes a minute.</div>
          <button className="btn-primary" style={{whiteSpace:"nowrap",padding:"0.6rem 1.2rem",fontSize:"0.82rem"}} onClick={onNeedAuth}>Sign up free →</button>
        </div>
      )}
      <div className="form-card">
        {error && <div style={{color:"var(--rose)",fontSize:"0.85rem",marginBottom:"1rem",padding:"0.75rem",background:"#FFF0F3",borderRadius:"10px"}}>{error}</div>}
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Your Name *</label><input className="form-input" placeholder="Mia Johnson" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></div>
          <div className="form-group"><label className="form-label">Email *</label><input className="form-input" type="email" placeholder="mia@gmail.com" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} /></div>
          <div className="form-group"><label className="form-label">Project Name *</label><input className="form-input" placeholder="The Wellness App" value={form.projectName} onChange={e=>setForm(f=>({...f,projectName:e.target.value}))} /></div>
          <div className="form-group"><label className="form-label">Industry</label><select className="form-select" value={form.industry} onChange={e=>setForm(f=>({...f,industry:e.target.value}))}><option value="">Select...</option>{INDUSTRIES.map(i=><option key={i}>{i}</option>)}</select></div>
          <div className="form-group full"><label className="form-label">What are you building?</label><textarea className="form-textarea" placeholder="Describe your project, the problem it solves, and where you're at..." value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} /></div>

          {/* Skills section with wizard option */}
          <div className="form-group full">
            <label className="form-label">Skills Needed</label>

            {/* Not sure toggle */}
            {skillMode === "choose" && (
              <div className="skill-mode-toggle">
                <div className="skill-mode-card" onClick={() => setSkillMode("wizard")}>
                  <div className="skill-mode-card-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  </div>
                  <div>
                    <div className="skill-mode-card-title">Not sure what skills you need?</div>
                    <div className="skill-mode-card-desc">Answer 5 quick questions and we'll suggest the right skills for your project</div>
                  </div>
                  <div style={{color:"var(--rose)",fontWeight:700,fontSize:"0.85rem",flexShrink:0}}>Help me →</div>
                </div>
              </div>
            )}

            {/* Wizard */}
            {skillMode === "wizard" && (
              <SkillHelperWizard onDone={handleWizardDone} onSkip={() => setSkillMode("choose")} />
            )}

            {/* Selected skills summary */}
            {form.skills.length > 0 && skillMode === "choose" && (
              <div className="skill-selected-summary">
                <span className="skill-selected-label">Selected skills:</span>
                <div style={{display:"flex",flexWrap:"wrap",gap:"0.4rem",marginTop:"0.4rem"}}>
                  {form.skills.map(s => (
                    <span key={s} className="skill-pick-btn selected" style={{cursor:"pointer"}} onClick={() => toggle(s)}>{s} ✕</span>
                  ))}
                </div>
                <button className="skill-wizard-skip" style={{marginTop:"0.75rem"}} onClick={() => setForm(f=>({...f,skills:[]}))}>Clear all</button>
              </div>
            )}

            {/* Full picker */}
            {skillMode === "choose" && (
              <div style={{marginTop: form.skills.length > 0 ? "1rem" : "0"}}>
                <GroupedSkillPicker selected={form.skills} onToggle={s=>toggle(s)} />
              </div>
            )}
          </div>

          <div className="form-group"><label className="form-label">Timeline</label><select className="form-select" value={form.urgency} onChange={e=>setForm(f=>({...f,urgency:e.target.value}))}><option value="">Select...</option>{URGENCY.map(u=><option key={u}>{u}</option>)}</select></div>
          <div className="form-group"><label className="form-label">Opportunity Type</label><select className="form-select" value={form.paid} onChange={e=>setForm(f=>({...f,paid:e.target.value}))}><option value="">Select...</option><option>Paid</option><option>Volunteer</option><option>Equity / Revenue share</option><option>Let's talk</option></select></div>
          <div className="form-group"><label className="form-label">Budget Range</label><select className="form-select" value={form.budgetRange||""} onChange={e=>setForm(f=>({...f,budgetRange:e.target.value}))}><option value="">Select...</option><option>Under $500</option><option>$500 - $2,000</option><option>$2,000 - $5,000</option><option>$5,000 - $15,000</option><option>$15,000+</option><option>Equity only</option><option>Let's discuss</option></select></div>
          <div className="form-group"><label className="form-label">Project Stage</label><select className="form-select" value={form.projectStage||""} onChange={e=>setForm(f=>({...f,projectStage:e.target.value}))}><option value="">Select...</option><option>Just an idea</option><option>I have a design / wireframe</option><option>Building MVP</option><option>MVP built -- need to improve</option><option>Live -- scaling up</option></select></div>
          <div className="form-group full"><label className="form-label">Your City</label><select className="form-select" value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))}><option value="">Select...</option>{CITIES.map(c=><option key={c}>{c}</option>)}</select></div>
          <div className="form-group full"><label className="form-label">Work Preference</label><select className="form-select" value={form.workPreference} onChange={e=>setForm(f=>({...f,workPreference:e.target.value}))}><option value="">Select...</option><option>Remote only</option><option>In-person only</option><option>Hybrid -- remote & in-person</option><option>No preference</option></select></div>
          <div className="form-group full"><label className="form-label">About you as a founder</label><textarea className="form-textarea" placeholder="Tell devs a bit about you. What drives you? What's your background? Why this project?" value={form.founderBio||""} onChange={e=>setForm(f=>({...f,founderBio:e.target.value}))}/></div>

          {/* Project links */}
          <div className="form-group full">
            <label className="form-label">Project Links</label>
            <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
              {[
                {key:"websiteUrl", label:"Website", placeholder:"https://yourproject.com"},
                {key:"instagramUrl", label:"Instagram", placeholder:"https://instagram.com/yourproject"},
                {key:"linkedinUrl", label:"LinkedIn", placeholder:"https://linkedin.com/company/yourproject"},
              ].map(({key,label,placeholder})=>(
                <div key={key} style={{position:"relative"}}>
                  <span style={{position:"absolute",left:"0.9rem",top:"50%",transform:"translateY(-50%)",fontSize:"0.75rem",fontWeight:700,color:"var(--muted)",width:"90px"}}>{label}</span>
                  <input className="form-input" style={{paddingLeft:"6rem"}} placeholder={placeholder} value={form[key]||""} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}/>
                </div>
              ))}
            </div>
          </div>
            <label className="form-label">What kind of dev are you looking for?</label>
            <div style={{fontSize:"0.75rem",color:"var(--muted)",marginBottom:"0.6rem"}}>Beyond skills -- what's the vibe you need?</div>
            <textarea className="form-textarea" placeholder="e.g. Someone who can take initiative, loves early-stage chaos, and doesn't need hand-holding. Ideally someone who's built consumer apps before..." value={form.devVibe||""} onChange={e=>setForm(f=>({...f,devVibe:e.target.value}))}/>
          </div>
          <div className="form-group full">
            <label className="form-label">Perks & benefits -- pick all that apply</label>
            <div className="skill-picker">
              {["Flexible hours","Fully remote","Equity / ownership","Exciting mission","Fast-moving team","Be part of something from the start","Work on your own schedule","Potential for long-term role","Revenue share","Build your portfolio","Mentorship from experienced founders","Creative freedom"].map(perk=>(
                <button key={perk} className={`skill-pick-btn ${(form.perks||[]).includes(perk)?"selected":""}`}
                  onClick={()=>setForm(f=>({...f,perks:(f.perks||[]).includes(perk)?(f.perks||[]).filter(x=>x!==perk):[...(f.perks||[]),perk]}))}>
                  {perk}
                </button>
              ))}
            </div>
          </div>

          {/* Founder icebreakers */}
          <div className="form-group full">
            <label className="form-label">Founder prompts ✨</label>
            <div style={{fontSize:"0.75rem",color:"var(--muted)",marginBottom:"0.75rem"}}>Help devs get to know you and your vision -- answer 1 or more</div>
            {[
              { key:"founderPrompt1", prompt:"This project started because...", placeholder:"e.g. I couldn't find a tool that did X, so I decided to build it..." },
              { key:"founderPrompt2", prompt:"The problem I'm obsessed with solving is...", placeholder:"e.g. The fact that 80% of small businesses still manage bookings by phone..." },
              { key:"founderPrompt3", prompt:"The kind of dev I vibe best with...", placeholder:"e.g. Someone who asks good questions, challenges my assumptions, and loves a Slack meme..." },
              { key:"founderPrompt4", prompt:"If this works, in 5 years we'll...", placeholder:"e.g. Be the go-to platform for women-led businesses across Southeast Asia..." },
            ].map(({key, prompt, placeholder})=>(
              <div key={key} style={{background:"var(--cream)",border:"1.5px solid var(--border)",borderRadius:"14px",padding:"1rem 1.1rem",marginBottom:"0.75rem"}}>
                <div style={{fontSize:"0.82rem",fontWeight:700,color:"var(--black)",marginBottom:"0.5rem"}}>{prompt}</div>
                <textarea className="form-textarea" style={{minHeight:"65px",background:"white",border:"1px solid var(--border)"}}
                  placeholder={placeholder}
                  value={form[key]||""}
                  onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}/>
              </div>
            ))}
          </div>

          {/* Project images */}
          {user && (
            <div className="form-group full">
              <label className="form-label">Project Images</label>
              <div style={{fontSize:"0.75rem",color:"var(--muted)",marginBottom:"0.5rem"}}>First image will be used as the cover on browse cards</div>
              <PortfolioImageUpload userId={user.id} images={form.projectImages||[]} onUpdate={imgs=>setForm(f=>({...f,projectImages:imgs}))}/>
            </div>
          )}
        </div>
        <button className="form-submit" onClick={handleSubmit} disabled={loading}>{loading ? "Submitting..." : "Submit to the deck"}</button>
      </div>
  );
}

function SavedPanel({ saved, onUnsave, onClose }) {
  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={e=>e.stopPropagation()}>
        <div className="panel-header">
          <div className="panel-title">Your Shortlist ♥</div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        {saved.length === 0 ? (
          <div className="empty-saved">
            <div className="empty-saved-icon">
              <svg viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
            </div>
            <div className="empty-saved-text">No saved devs yet.<br />Tap the heart on any profile to shortlist them.</div>
          </div>
        ) : saved.map(dev => (
          <div key={dev.id} className="saved-item">
            <div className="saved-item-avatar">{initials(dev.name)}</div>
            <div className="saved-item-info">
              <div className="saved-item-name">{dev.name}</div>
              <div className="saved-item-meta">{dev.level} · {dev.city}</div>
            </div>
            <button className="unsave-btn" onClick={()=>onUnsave(dev)}>✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── AUTH MODAL ────────────────────────────────────────────────────────────────
function AuthModal({ onClose, onSuccess, defaultRole, onForgotPassword }) {
  const [mode, setMode] = useState("signup");
  const [role, setRole] = useState(defaultRole || "developer");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sync role if defaultRole changes after mount
  useEffect(() => {
    if (defaultRole) setRole(defaultRole);
  }, [defaultRole]);

  const handleSignup = async () => {
    if (!email || !password || !name) { setError("Please fill in all fields."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true); setError(null);
    const { data, error: authError } = await supabase.auth.signUp({ email, password });
    if (authError) { setError(authError.message); setLoading(false); return; }
    if (data.user) {
      await supabase.from("profiles").insert([{ id: data.user.id, role, email, name }]);
      // Send welcome email
      const welcome = emailTemplates.welcome(name, role);
      await sendEmail({ to: email, ...welcome });
    }
    setLoading(false);
    onSuccess({ user: data.user, role, name });
  };

  const handleLogin = async () => {
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError(null);
    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) { setError("Incorrect email or password."); setLoading(false); return; }
    const { data: profile } = await supabase.from("profiles").select("*").eq("id", data.user.id).single();
    setLoading(false);
    onSuccess({ user: data.user, role: profile?.role, name: profile?.name });
  };

  return (
    <div className="auth-overlay" onClick={onClose}>
      <div className="auth-box" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="auth-logo">The Match<span>Deck</span></div>
        <div className="auth-title">{mode === "signup" ? "Create your account" : "Welcome back"}</div>
        <div className="auth-sub">{mode === "signup" ? "Join the deck and start connecting." : "Log in to view your dashboard."}</div>

        {error && <div className="auth-error">{error}</div>}

        {mode === "signup" && (
          <>
            {defaultRole ? (
              // Role is pre-set -- show it as a confirmation, not a choice
              <div style={{background:"var(--cream)",border:"1.5px solid var(--border)",borderRadius:"12px",padding:"0.85rem 1rem",marginBottom:"1rem",display:"flex",alignItems:"center",gap:"0.75rem"}}>
                <span style={{fontSize:"1.1rem"}}>{defaultRole === "developer" ? "👩‍💻" : "💡"}</span>
                <div>
                  <div style={{fontWeight:700,fontSize:"0.85rem"}}>Signing up as a {defaultRole === "developer" ? "Developer" : "Project Owner"}</div>
                  <button style={{background:"none",border:"none",color:"var(--rose)",fontSize:"0.75rem",cursor:"pointer",padding:0,fontFamily:"'DM Sans',sans-serif"}}
                    onClick={()=>setRole(defaultRole === "developer" ? "owner" : "developer")}>
                    Switch to {defaultRole === "developer" ? "Project Owner" : "Developer"} instead
                  </button>
                </div>
              </div>
            ) : (
              <div style={{marginBottom:"1rem"}}>
                <div className="form-label" style={{marginBottom:"0.5rem"}}>I am a...</div>
                <div className="auth-role-pick">
                  <div className={`auth-role-btn ${role==="developer"?"active":""}`} onClick={()=>setRole("developer")}>
                    <div className="auth-role-icon">👩‍💻</div>
                    <div className="auth-role-label">Developer</div>
                    <div className="auth-role-desc">I build things</div>
                  </div>
                  <div className={`auth-role-btn ${role==="owner"?"active":""}`} onClick={()=>setRole("owner")}>
                    <div className="auth-role-icon">💡</div>
                    <div className="auth-role-label">Project Owner</div>
                    <div className="auth-role-desc">I have an idea</div>
                  </div>
                </div>
              </div>
            )}
            <input className="auth-input" placeholder="Full name" value={name} onChange={e=>setName(e.target.value)} />
          </>
        )}

        <input className="auth-input" type="email" placeholder="Email address" value={email} onChange={e=>setEmail(e.target.value)} />
        <input className="auth-input" type="password" placeholder="Password (min 6 characters)" value={password} onChange={e=>setPassword(e.target.value)}
          onKeyDown={e => e.key === "Enter" && (mode === "signup" ? handleSignup() : handleLogin())} />

        <button className="auth-btn" onClick={mode==="signup" ? handleSignup : handleLogin} disabled={loading}>
          {loading ? "Please wait..." : mode==="signup" ? "Create account" : "Log in"}
        </button>

        <div className="auth-switch">
          {mode==="signup" ? (
            <>Already have an account? <button onClick={()=>{setMode("login");setError(null);}}>Log in</button></>
          ) : (
            <>
              <div>Don't have an account? <button onClick={()=>{setMode("signup");setError(null);}}>Sign up</button></div>
              <div style={{marginTop:"0.5rem"}}><button onClick={onForgotPassword} style={{color:"var(--muted)"}}>Forgot your password?</button></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── EMAIL HELPER ─────────────────────────────────────────────────────────────
async function sendEmail({ to, subject, html }) {
  try {
    await fetch("https://api.resend.com/emails", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${process.env.REACT_APP_RESEND_API_KEY}`,
      },
      body: JSON.stringify({
        from: "The Match Deck <hello@thematchdeck.com>",
        to,
        subject,
        html,
      }),
    });
  } catch (e) {
    console.warn("Email send failed:", e);
  }
}

const emailTemplates = {
  welcome: (name, role) => ({
    subject: "Welcome to The Match Deck",
    html: `<div style="font-family:sans-serif;max-width:560px;margin:0 auto;padding:2rem">
      <h1 style="font-size:1.5rem;margin-bottom:0.5rem">Welcome, ${name}!</h1>
      <p style="color:#555;line-height:1.7">You're now part of The Match Deck -- Australia's platform connecting ${role === "developer" ? "talented developers with project founders" : "project founders with talented developers"}.</p>
      <p style="color:#555;line-height:1.7">${role === "developer" ? "Your profile is live and founders can already find you. Make sure your past projects section is detailed -- it's the #1 thing founders look at." : "Submit your first project and we'll start matching you with the right developer."}</p>
      <a href="https://thematchdeck.netlify.app" style="display:inline-block;margin-top:1rem;background:#E8355A;color:white;padding:0.75rem 1.5rem;border-radius:100px;text-decoration:none;font-weight:700">Go to The Match Deck →</a>
      <p style="margin-top:2rem;color:#aaa;font-size:0.8rem">The Match Deck · Built for the Australian women's tech community</p>
    </div>`
  }),
  newDevProfile: (name, email) => ({
    subject: `New developer profile: ${name}`,
    html: `<div style="font-family:sans-serif;max-width:560px;margin:0 auto;padding:2rem">
      <h2>New developer profile submitted</h2>
      <p><strong>${name}</strong> (${email}) just joined the deck as a developer.</p>
      <a href="https://supabase.com" style="display:inline-block;margin-top:1rem;background:#0D0D0D;color:white;padding:0.75rem 1.5rem;border-radius:100px;text-decoration:none;font-weight:700">View in Supabase →</a>
    </div>`
  }),
  newProject: (projectName, founderName, email) => ({
    subject: `New project submitted: ${projectName}`,
    html: `<div style="font-family:sans-serif;max-width:560px;margin:0 auto;padding:2rem">
      <h2>New project submitted</h2>
      <p><strong>${founderName}</strong> (${email}) just submitted <strong>${projectName}</strong>.</p>
      <a href="https://supabase.com" style="display:inline-block;margin-top:1rem;background:#0D0D0D;color:white;padding:0.75rem 1.5rem;border-radius:100px;text-decoration:none;font-weight:700">View in Supabase →</a>
    </div>`
  }),
  interestInDev: (devName, devEmail, fromName, message) => ({
    subject: `Someone wants to connect with you on The Match Deck`,
    html: `<div style="font-family:sans-serif;max-width:560px;margin:0 auto;padding:2rem">
      <h2>You have a new connection request!</h2>
      <p>Hi ${devName}, <strong>${fromName}</strong> is interested in working with you.</p>
      ${message ? `<blockquote style="border-left:3px solid #E8355A;padding-left:1rem;color:#555;font-style:italic">"${message}"</blockquote>` : ""}
      <p style="color:#555">Log in to your dashboard to view their details and respond.</p>
      <a href="https://thematchdeck.netlify.app" style="display:inline-block;margin-top:1rem;background:#E8355A;color:white;padding:0.75rem 1.5rem;border-radius:100px;text-decoration:none;font-weight:700">View on The Match Deck →</a>
    </div>`
  }),
  interestInProject: (founderEmail, founderName, devName, projectName, message) => ({
    subject: `A developer applied to ${projectName}`,
    html: `<div style="font-family:sans-serif;max-width:560px;margin:0 auto;padding:2rem">
      <h2>New application for ${projectName}</h2>
      <p>Hi ${founderName}, <strong>${devName}</strong> has expressed interest in your project.</p>
      ${message ? `<blockquote style="border-left:3px solid #E8355A;padding-left:1rem;color:#555;font-style:italic">"${message}"</blockquote>` : ""}
      <p style="color:#555">Log in to your dashboard to view their profile and respond.</p>
      <a href="https://thematchdeck.netlify.app" style="display:inline-block;margin-top:1rem;background:#E8355A;color:white;padding:0.75rem 1.5rem;border-radius:100px;text-decoration:none;font-weight:700">View on The Match Deck →</a>
    </div>`
  }),
};

// ── AVATAR UPLOAD ─────────────────────────────────────────────────────────────
function AvatarUpload({ userId, currentUrl, onUpload }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl);
  const [uploadError, setUploadError] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setUploadError("File must be under 10MB."); return; }
    setUploading(true); setUploadError(null);
    const ext = file.name.split(".").pop().toLowerCase() || "jpg";
    const path = `${userId}/avatar-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("Avatars").upload(path, file, {
      upsert: false,
      contentType: file.type || "image/jpeg",
    });
    if (error) {
      setUploadError(error.message);
    } else {
      const { data } = supabase.storage.from("Avatars").getPublicUrl(path);
      // Add cache buster so new photo shows immediately
      const urlWithBuster = `${data.publicUrl}?t=${Date.now()}`;
      setPreview(urlWithBuster);
      onUpload(data.publicUrl);
    }
    setUploading(false);
  };

  return (
    <div>
      <div className="avatar-upload">
        <div className="avatar-preview">
          {preview ? <img src={preview} alt="avatar" /> : "?"}
        </div>
        <div>
          <button className="avatar-upload-btn" onClick={() => fileRef.current.click()} disabled={uploading}>
            {uploading ? "Uploading..." : preview ? "Change photo" : "Upload photo"}
          </button>
          <div style={{fontSize:"0.72rem",color:"var(--muted)",marginTop:"0.3rem"}}>Any image format, max 10MB</div>
          <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleFile} />
        </div>
      </div>
      {uploadError && <div style={{color:"var(--rose)",fontSize:"0.78rem",marginTop:"0.35rem"}}>{uploadError}</div>}
    </div>
  );
}

// ── PORTFOLIO IMAGE UPLOAD ────────────────────────────────────────────────────
function PortfolioImageUpload({ userId, images, onUpdate }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const MAX = 4;

  const handleFile = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (images.length + files.length > MAX) { setError(`Max ${MAX} images allowed.`); return; }
    setUploading(true); setError(null);
    const newUrls = [...images];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { setError("Each image must be under 10MB."); continue; }
      const ext = file.name.split(".").pop().toLowerCase();
      const path = `${userId}/portfolio-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: uploadError } = await supabase.storage.from("portfolio").upload(path, file, { contentType: file.type });
      if (!uploadError) {
        const { data } = supabase.storage.from("portfolio").getPublicUrl(path);
        newUrls.push(data.publicUrl);
      }
    }
    onUpdate(newUrls);
    setUploading(false);
  };

  const removeImage = (idx) => onUpdate(images.filter((_, i) => i !== idx));

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"0.75rem",marginBottom:"0.75rem"}}>
        {images.map((url, i) => (
          <div key={i} style={{position:"relative",borderRadius:"14px",overflow:"hidden",aspectRatio:"4/3",background:"var(--cream)"}}>
            <img src={url} alt={`Portfolio ${i+1}`} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            <button onClick={()=>removeImage(i)} style={{position:"absolute",top:"0.5rem",right:"0.5rem",background:"rgba(13,13,13,0.7)",color:"white",border:"none",borderRadius:"50%",width:28,height:28,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.85rem"}}>✕</button>
          </div>
        ))}
        {images.length < MAX && (
          <div onClick={()=>fileRef.current.click()} style={{borderRadius:"14px",border:"2px dashed var(--border)",aspectRatio:"4/3",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",background:"var(--cream)",transition:"all 0.2s",gap:"0.5rem"}}
            onMouseOver={e=>{e.currentTarget.style.borderColor="var(--rose)";e.currentTarget.style.background="white";}}
            onMouseOut={e=>{e.currentTarget.style.borderColor="var(--border)";e.currentTarget.style.background="var(--cream)";}}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.75" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            <span style={{fontSize:"0.75rem",color:"var(--muted)",fontWeight:600}}>{uploading?"Uploading...":"Add photo"}</span>
          </div>
        )}
      </div>
      {error && <div style={{color:"var(--rose)",fontSize:"0.78rem",marginBottom:"0.5rem"}}>{error}</div>}
      <input ref={fileRef} type="file" accept="image/*" multiple style={{display:"none"}} onChange={handleFile}/>
      <div style={{fontSize:"0.72rem",color:"var(--muted)"}}>Any image format · Max 10MB each · {images.length}/{MAX} added</div>
    </div>
  );
}

// ── IMAGE LIGHTBOX ────────────────────────────────────────────────────────────
function Lightbox({ url, onClose }) {
  if (!url) return null;
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:"1rem"}}>
      <button onClick={onClose} style={{position:"absolute",top:"1.25rem",right:"1.25rem",background:"rgba(255,255,255,0.1)",border:"none",color:"white",width:40,height:40,borderRadius:"50%",fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
      <img src={url} alt="Full size" style={{maxWidth:"100%",maxHeight:"90vh",borderRadius:"12px",objectFit:"contain"}} onClick={e=>e.stopPropagation()}/>
    </div>
  );
}

// ── INTEREST MODAL ────────────────────────────────────────────────────────────
function InterestModal({ dev, project, user, onClose }) {
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const target = dev || project;
  const targetName = dev ? dev.name : project?.name || project?.project_name;

  const handleSend = async () => {
    setLoading(true);
    // Save to database
    await supabase.from("interests").insert([{
      from_user_id: user.id,
      developer_id: dev?.id || null,
      project_id: project?.id || null,
      message,
    }]);

    // Get sender's profile name
    const { data: senderProfile } = await supabase.from("profiles").select("name").eq("id", user.id).single();
    const fromName = senderProfile?.name || user.email;

    // Send email notification to the dev or project owner
    if (dev && dev.email) {
      const tpl = emailTemplates.interestInDev(dev.name, dev.email, fromName, message);
      await sendEmail({ to: dev.email, ...tpl });
    }
    if (project && project.founder_email) {
      const tpl = emailTemplates.interestInProject(project.founder_email, project.founder_name, fromName, project.project_name || project.name, message);
      await sendEmail({ to: project.founder_email, ...tpl });
    }

    setSent(true);
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="interest-modal" onClick={e=>e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        {sent ? (
          <div className="success-state">
            <div className="success-icon">✦</div>
            <div className="success-title">Interest sent!</div>
            <div className="success-sub">We've let {targetName} know you're interested. They'll be in touch soon.</div>
            <button className="btn-primary" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <div className="modal-name">Connect with {targetName}</div>
            <div className="modal-meta" style={{marginBottom:"0.5rem"}}>Add a short message to introduce yourself</div>
            <textarea className="interest-textarea"
              placeholder={dev
                ? `Hi ${targetName}, I'm working on a project and think your skills would be a great fit...`
                : `Hi, I'm interested in your project and would love to discuss how I can help...`}
              value={message} onChange={e=>setMessage(e.target.value)} rows={4} />
            <button className="modal-connect" style={{width:"100%"}} onClick={handleSend} disabled={loading}>
              {loading ? "Sending..." : "Send interest"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
function Dashboard({ user, profile, onNavigate, onLogout }) {
  const [tab, setTab] = useState("profile");
  const [myDev, setMyDev] = useState(null);
  const [myProjects, setMyProjects] = useState([]);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDev, setEditingDev] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [devForm, setDevForm] = useState({});
  const [projForm, setProjForm] = useState({});

  useEffect(() => {
    async function load() {
      if (profile?.role === "developer") {
        const { data } = await supabase.from("developers").select("*").eq("user_id", user.id).single();
        setMyDev(data);
        if (data) {
          setDevForm({ name:data.name||"", email:data.email||"", city:data.city||"", level:data.level||"", bio:data.bio||"", availability:data.availability||"", portfolio_url:data.portfolio_url||"", linkedin_url:data.linkedin_url||"", github_url:data.github_url||"", instagram_url:data.instagram_url||"", other_url:data.other_url||"", past_projects:data.past_projects||"", skills:data.skills||[], industries:data.industries||[], avatar_url:data.avatar_url||"", portfolio_images:data.portfolio_images||[], work_preference:data.work_preference||"", personality_tags:data.personality_tags||[], icebreaker:data.icebreaker||"", icebreaker2:data.icebreaker2||"", icebreaker3:data.icebreaker3||"", icebreaker4:data.icebreaker4||"" });
          const { data: ints } = await supabase.from("interests").select("*").eq("developer_id", data.id);
          setInterests(ints || []);
        }
      } else {
        const { data } = await supabase.from("projects").select("*").eq("user_id", user.id);
        setMyProjects(data || []);
        if (data?.length > 0) {
          const ids = data.map(p => p.id);
          const { data: ints } = await supabase.from("interests").select("*").in("project_id", ids);
          setInterests(ints || []);
        }
      }
      setLoading(false);
    }
    load();
  }, [user, profile]);

  const saveDev = async () => {
    setSaving(true); setSaveMsg(null);
    const { error } = await supabase.from("developers").update({
      name:devForm.name, email:devForm.email, city:devForm.city, level:devForm.level,
      bio:devForm.bio, availability:devForm.availability, work_preference:devForm.work_preference,
      portfolio_url:devForm.portfolio_url, linkedin_url:devForm.linkedin_url,
      github_url:devForm.github_url, instagram_url:devForm.instagram_url,
      other_url:devForm.other_url, past_projects:devForm.past_projects,
      skills:devForm.skills, industries:devForm.industries,
      avatar_url:devForm.avatar_url,
      portfolio_images:devForm.portfolio_images||[],
      personality_tags:devForm.personality_tags||[],
      icebreaker:devForm.icebreaker||"",
      icebreaker2:devForm.icebreaker2||"",
      icebreaker3:devForm.icebreaker3||"",
      icebreaker4:devForm.icebreaker4||"",
    }).eq("id", myDev.id);
    if (error) { setSaveMsg("Error saving: " + error.message); }
    else { setMyDev({...myDev,...devForm}); setEditingDev(false); setSaveMsg("Profile saved!"); setTimeout(()=>setSaveMsg(null),3000); }
    setSaving(false);
  };

  const saveProject = async () => {
    setSaving(true); setSaveMsg(null);
    const { error } = await supabase.from("projects").update({
      project_name:projForm.project_name, description:projForm.description,
      industry:projForm.industry, city:projForm.city, urgency:projForm.urgency,
      paid_type:projForm.paid_type, work_preference:projForm.work_preference,
      budget_range:projForm.budget_range||"", project_stage:projForm.project_stage||"",
      skills_needed:projForm.skills_needed,
      project_images:projForm.project_images||[],
      founder_bio:projForm.founder_bio||"", dev_vibe:projForm.dev_vibe||"",
      perks:projForm.perks||[],
      website_url:projForm.website_url||"", instagram_url:projForm.instagram_url||"", linkedin_url:projForm.linkedin_url||"",
      founder_prompt1:projForm.founder_prompt1||"", founder_prompt2:projForm.founder_prompt2||"",
      founder_prompt3:projForm.founder_prompt3||"", founder_prompt4:projForm.founder_prompt4||"",
    }).eq("id", editingProject.id);
    if (error) { setSaveMsg("Error saving: " + error.message); }
    else { setMyProjects(ps=>ps.map(p=>p.id===editingProject.id?{...p,...projForm}:p)); setEditingProject(null); setSaveMsg("Project saved!"); setTimeout(()=>setSaveMsg(null),3000); }
    setSaving(false);
  };

  const toggleSkill = s => setDevForm(f=>({...f,skills:f.skills.includes(s)?f.skills.filter(x=>x!==s):[...f.skills,s]}));
  const toggleIndustry = i => setDevForm(f=>({...f,industries:f.industries.includes(i)?f.industries.filter(x=>x!==i):[...f.industries,i]}));
  const toggleProjSkill = s => setProjForm(f=>({...f,skills_needed:(f.skills_needed||[]).includes(s)?(f.skills_needed||[]).filter(x=>x!==s):[...(f.skills_needed||[]),s]}));
  const isDev = profile?.role === "developer";
  const displayName = profile?.name || user.email;

  const openProjEdit = (p) => {
    setEditingProject(p);
    setProjForm({
      project_name:p.project_name||"", description:p.description||"", industry:p.industry||"",
      city:p.city||"", urgency:p.urgency||"", paid_type:p.paid_type||"",
      budget_range:p.budget_range||"", project_stage:p.project_stage||"",
      skills_needed:p.skills_needed||[], work_preference:p.work_preference||"",
      project_images:p.project_images||[], founder_bio:p.founder_bio||"",
      dev_vibe:p.dev_vibe||"", perks:p.perks||[],
      website_url:p.website_url||"", instagram_url:p.instagram_url||"", linkedin_url:p.linkedin_url||"",
      founder_prompt1:p.founder_prompt1||"", founder_prompt2:p.founder_prompt2||"",
      founder_prompt3:p.founder_prompt3||"", founder_prompt4:p.founder_prompt4||"",
    });
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="dashboard-avatar">
          {myDev?.avatar_url ? <img src={myDev.avatar_url} alt={displayName}/> : initials(displayName)}
        </div>
        <div>
          <div className="dashboard-name">{displayName}</div>
          <div className="dashboard-role">{isDev ? "Developer" : "Project Owner"}</div>
        </div>
        <button className="dash-btn" style={{marginLeft:"auto"}} onClick={onLogout}>Log out</button>
      </div>

      {saveMsg && <div style={{background:saveMsg.startsWith("Error")?"#FFF0F3":"#F0FFF4",border:`1px solid ${saveMsg.startsWith("Error")?"var(--rose)":"#86efac"}`,borderRadius:"10px",padding:"0.65rem 1rem",marginBottom:"1rem",fontSize:"0.85rem",color:saveMsg.startsWith("Error")?"var(--rose)":"#166534"}}>{saveMsg}</div>}

      <div className="dashboard-tabs">
        <button className={`dashboard-tab ${tab==="profile"?"active":""}`} onClick={()=>{setTab("profile");setEditingDev(false);setEditingProject(null);}}>{isDev?"My Profile":"My Projects"}</button>
        <button className={`dashboard-tab ${tab==="interests"?"active":""}`} onClick={()=>setTab("interests")}>Expressions of Interest {interests.length>0&&`(${interests.length})`}</button>
      </div>

      {loading ? (
        <div style={{color:"var(--muted)",textAlign:"center",padding:"3rem"}}>Loading...</div>
      ) : tab === "profile" ? (
        isDev ? (
          myDev ? (
            editingDev ? (
              <div className="dash-card">
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1.5rem"}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.1rem",fontWeight:700}}>Edit your profile</div>
                  <button className="dash-btn" onClick={()=>setEditingDev(false)}>Cancel</button>
                </div>
                <div className="form-grid">
                  <div className="form-group full"><label className="form-label">Profile Photo</label><AvatarUpload userId={user.id} currentUrl={devForm.avatar_url} onUpload={url=>setDevForm(f=>({...f,avatar_url:url}))}/></div>
                  <div className="form-group"><label className="form-label">Full Name</label><input className="form-input" value={devForm.name} onChange={e=>setDevForm(f=>({...f,name:e.target.value}))}/></div>
                  <div className="form-group"><label className="form-label">City</label><select className="form-select" value={devForm.city} onChange={e=>setDevForm(f=>({...f,city:e.target.value}))}><option value="">Select...</option>{CITIES.map(c=><option key={c}>{c}</option>)}</select></div>
                  <div className="form-group"><label className="form-label">Level</label><select className="form-select" value={devForm.level} onChange={e=>setDevForm(f=>({...f,level:e.target.value}))}><option value="">Select...</option><option>Junior</option><option>Mid-level</option><option>Senior</option></select></div>
                  <div className="form-group"><label className="form-label">Availability</label><select className="form-select" value={devForm.availability} onChange={e=>setDevForm(f=>({...f,availability:e.target.value}))}><option value="">Select...</option><option>Available now</option><option>Available in 2 weeks</option><option>Available in 1 month</option><option>Available for part-time only</option></select></div>
                  <div className="form-group"><label className="form-label">Work Preference</label><select className="form-select" value={devForm.work_preference||""} onChange={e=>setDevForm(f=>({...f,work_preference:e.target.value}))}><option value="">Select...</option><option>Remote only</option><option>In-person only</option><option>Hybrid -- remote & in-person</option><option>No preference</option></select></div>
                  <div className="form-group full"><label className="form-label">Bio</label><textarea className="form-textarea" value={devForm.bio} onChange={e=>setDevForm(f=>({...f,bio:e.target.value}))}/></div>
                  <div className="form-group full"><label className="form-label">Portfolio URL</label><input className="form-input" value={devForm.portfolio_url} onChange={e=>setDevForm(f=>({...f,portfolio_url:e.target.value}))}/></div>
                  <div className="form-group full"><label className="form-label">LinkedIn URL</label><input className="form-input" value={devForm.linkedin_url} onChange={e=>setDevForm(f=>({...f,linkedin_url:e.target.value}))}/></div>
                  <div className="form-group full"><label className="form-label">GitHub URL</label><input className="form-input" value={devForm.github_url} onChange={e=>setDevForm(f=>({...f,github_url:e.target.value}))}/></div>
                  <div className="form-group full"><label className="form-label">Instagram URL</label><input className="form-input" value={devForm.instagram_url} onChange={e=>setDevForm(f=>({...f,instagram_url:e.target.value}))}/></div>
                  <div className="form-group full"><label className="form-label">Past Projects ✦</label><textarea className="form-textarea" style={{minHeight:"100px",borderColor:"var(--rose)"}} value={devForm.past_projects} onChange={e=>setDevForm(f=>({...f,past_projects:e.target.value}))}/></div>
                  <div className="form-group full"><label className="form-label">Skills</label><GroupedSkillPicker selected={devForm.skills} onToggle={toggleSkill}/></div>
                  <div className="form-group full"><label className="form-label">Industries</label><div className="skill-picker">{INDUSTRIES.map(i=><button key={i} className={`skill-pick-btn ${devForm.industries.includes(i)?"selected":""}`} onClick={()=>toggleIndustry(i)}>{i}</button>)}</div></div>
                  <div className="form-group full">
                    <label className="form-label">Your vibe</label>
                    <div className="skill-picker">
                      {["I work best async","I love early-stage chaos","I'm a finisher, not just a starter","I care about design details","I move fast and iterate","I love a good brief","I prefer long-term projects","I'm great at explaining tech to non-techies","I'm a solo operator","I love collaborating","I build for impact","I'm obsessed with clean code"].map(tag=>(
                        <button key={tag} className={`skill-pick-btn ${(devForm.personality_tags||[]).includes(tag)?"selected":""}`}
                          onClick={()=>setDevForm(f=>({...f,personality_tags:(f.personality_tags||[]).includes(tag)?(f.personality_tags||[]).filter(x=>x!==tag):[...(f.personality_tags||[]),tag]}))}>
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group full">
                    <label className="form-label">Icebreakers</label>
                    {[
                      {key:"icebreaker",prompt:"The last thing I built was..."},
                      {key:"icebreaker2",prompt:"I'm proudest of..."},
                      {key:"icebreaker3",prompt:"My dream project is..."},
                      {key:"icebreaker4",prompt:"Outside of code, I'm known for..."},
                    ].map(({key,prompt})=>(
                      <div key={key} style={{background:"var(--cream)",border:"1.5px solid var(--border)",borderRadius:"14px",padding:"1rem",marginBottom:"0.75rem"}}>
                        <div style={{fontSize:"0.8rem",fontWeight:700,color:"var(--black)",marginBottom:"0.5rem"}}>{prompt}</div>
                        <textarea className="form-textarea" style={{minHeight:"60px",background:"white",border:"1px solid var(--border)"}} value={devForm[key]||""} onChange={e=>setDevForm(f=>({...f,[key]:e.target.value}))}/>
                      </div>
                    ))}
                  </div>
                  <div className="form-group full">
                    <label className="form-label">Portfolio Images</label>
                    <div style={{fontSize:"0.75rem",color:"var(--muted)",marginBottom:"0.6rem"}}>Up to 4 screenshots of your work</div>
                    <PortfolioImageUpload userId={user.id} images={devForm.portfolio_images||[]} onUpdate={imgs=>setDevForm(f=>({...f,portfolio_images:imgs}))}/>
                  </div>
                </div>
                <button className="form-submit" style={{marginTop:"1.5rem"}} onClick={saveDev} disabled={saving}>{saving?"Saving...":"Save changes"}</button>
              </div>
            ) : (
              <div className="dash-card">
                {myDev.avatar_url && <img src={myDev.avatar_url} alt={myDev.name} style={{width:64,height:64,borderRadius:16,objectFit:"cover",marginBottom:"1rem"}}/>}
                <div className="dash-card-title">{myDev.name}</div>
                <div className="dash-card-meta">{myDev.level} · {myDev.city} · {myDev.availability}</div>
                <div style={{fontSize:"0.85rem",color:"var(--mid)",lineHeight:1.6,margin:"0.5rem 0 0.75rem"}}>{myDev.bio}</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:"0.4rem",marginBottom:"0.75rem"}}>{(myDev.skills||[]).map(s=><span key={s} className="skill-tag">{s}</span>)}</div>
                {myDev.past_projects && <div style={{fontSize:"0.82rem",color:"var(--mid)",background:"var(--cream)",borderRadius:10,padding:"0.75rem",marginBottom:"0.75rem",borderLeft:"3px solid var(--rose)"}}>✦ {myDev.past_projects}</div>}
                <div style={{display:"flex",flexWrap:"wrap",gap:"0.5rem",marginBottom:"1rem"}}>
                  {[{url:myDev.portfolio_url,label:"Portfolio"},{url:myDev.linkedin_url,label:"LinkedIn"},{url:myDev.github_url,label:"GitHub"},{url:myDev.instagram_url,label:"Instagram"}].filter(l=>l.url).map(l=>(
                    <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer" style={{fontSize:"0.75rem",fontWeight:700,color:"var(--rose)",textDecoration:"none",background:"#FFF0F3",padding:"0.3rem 0.7rem",borderRadius:"100px"}}>{l.label} ↗</a>
                  ))}
                </div>
                <div className="dash-card-actions">
                  <button className="dash-btn dash-btn-primary" onClick={()=>setEditingDev(true)}>Edit profile</button>
                  <button className="dash-btn" onClick={()=>onNavigate("browse-devs")}>Browse devs</button>
                </div>
              </div>
            )
          ) : (
            <div className="empty-dash">
              <div className="empty-dash-title">No profile yet</div>
              <div className="empty-dash-sub">Create your developer profile to start getting matched.</div>
              <button className="btn-primary" style={{marginTop:"1rem"}} onClick={()=>onNavigate("dev-signup")}>Create profile →</button>
            </div>
          )
        ) : (
          <div>
            {myProjects.map(p => (
              editingProject?.id === p.id ? (
                <div key={p.id} className="dash-card" style={{marginBottom:"1rem"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1.5rem"}}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.1rem",fontWeight:700}}>Edit project</div>
                    <button className="dash-btn" onClick={()=>setEditingProject(null)}>Cancel</button>
                  </div>
                  <div className="form-grid">
                    <div className="form-group full"><label className="form-label">Project Name</label><input className="form-input" value={projForm.project_name||""} onChange={e=>setProjForm(f=>({...f,project_name:e.target.value}))}/></div>
                    <div className="form-group"><label className="form-label">Industry</label><select className="form-select" value={projForm.industry||""} onChange={e=>setProjForm(f=>({...f,industry:e.target.value}))}><option value="">Select...</option>{INDUSTRIES.map(i=><option key={i}>{i}</option>)}</select></div>
                    <div className="form-group"><label className="form-label">City</label><select className="form-select" value={projForm.city||""} onChange={e=>setProjForm(f=>({...f,city:e.target.value}))}><option value="">Select...</option>{CITIES.map(c=><option key={c}>{c}</option>)}</select></div>
                    <div className="form-group"><label className="form-label">Timeline</label><select className="form-select" value={projForm.urgency||""} onChange={e=>setProjForm(f=>({...f,urgency:e.target.value}))}><option value="">Select...</option>{URGENCY.map(u=><option key={u}>{u}</option>)}</select></div>
                    <div className="form-group"><label className="form-label">Opportunity Type</label><select className="form-select" value={projForm.paid_type||""} onChange={e=>setProjForm(f=>({...f,paid_type:e.target.value}))}><option value="">Select...</option><option>Paid</option><option>Volunteer</option><option>Equity / Revenue share</option><option>Let's talk</option></select></div>
                    <div className="form-group"><label className="form-label">Budget Range</label><select className="form-select" value={projForm.budget_range||""} onChange={e=>setProjForm(f=>({...f,budget_range:e.target.value}))}><option value="">Select...</option><option>Under $500</option><option>$500 -- $2,000</option><option>$2,000 -- $5,000</option><option>$5,000 -- $15,000</option><option>$15,000+</option><option>Equity only</option><option>Let's discuss</option></select></div>
                    <div className="form-group"><label className="form-label">Project Stage</label><select className="form-select" value={projForm.project_stage||""} onChange={e=>setProjForm(f=>({...f,project_stage:e.target.value}))}><option value="">Select...</option><option>Just an idea</option><option>I have a design / wireframe</option><option>Building MVP</option><option>MVP built -- need to improve</option><option>Live -- scaling up</option></select></div>
                    <div className="form-group"><label className="form-label">Work Preference</label><select className="form-select" value={projForm.work_preference||""} onChange={e=>setProjForm(f=>({...f,work_preference:e.target.value}))}><option value="">Select...</option><option>Remote only</option><option>In-person only</option><option>Hybrid -- remote & in-person</option><option>No preference</option></select></div>
                    <div className="form-group full"><label className="form-label">Description</label><textarea className="form-textarea" value={projForm.description||""} onChange={e=>setProjForm(f=>({...f,description:e.target.value}))}/></div>
                    <div className="form-group full"><label className="form-label">About you as a founder</label><textarea className="form-textarea" value={projForm.founder_bio||""} onChange={e=>setProjForm(f=>({...f,founder_bio:e.target.value}))}/></div>
                    <div className="form-group full"><label className="form-label">What kind of dev are you looking for?</label><textarea className="form-textarea" value={projForm.dev_vibe||""} onChange={e=>setProjForm(f=>({...f,dev_vibe:e.target.value}))}/></div>
                    <div className="form-group full">
                      <label className="form-label">Project Links</label>
                      <div style={{display:"flex",flexDirection:"column",gap:"0.6rem"}}>
                        {[{key:"website_url",label:"Website"},{key:"instagram_url",label:"Instagram"},{key:"linkedin_url",label:"LinkedIn"}].map(({key,label})=>(
                          <div key={key} style={{position:"relative"}}>
                            <span style={{position:"absolute",left:"0.9rem",top:"50%",transform:"translateY(-50%)",fontSize:"0.75rem",fontWeight:700,color:"var(--muted)",width:"90px"}}>{label}</span>
                            <input className="form-input" style={{paddingLeft:"6rem"}} value={projForm[key]||""} onChange={e=>setProjForm(f=>({...f,[key]:e.target.value}))}/>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="form-group full"><label className="form-label">Skills Needed</label><GroupedSkillPicker selected={projForm.skills_needed||[]} onToggle={toggleProjSkill}/></div>
                    <div className="form-group full">
                      <label className="form-label">Perks</label>
                      <div className="skill-picker">
                        {["Flexible hours","Fully remote","Equity / ownership","Exciting mission","Fast-moving team","Be part of something from the start","Work on your own schedule","Potential for long-term role","Revenue share","Build your portfolio","Mentorship from experienced founders","Creative freedom"].map(perk=>(
                          <button key={perk} className={`skill-pick-btn ${(projForm.perks||[]).includes(perk)?"selected":""}`}
                            onClick={()=>setProjForm(f=>({...f,perks:(f.perks||[]).includes(perk)?(f.perks||[]).filter(x=>x!==perk):[...(f.perks||[]),perk]}))}>
                            {perk}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="form-group full">
                      <label className="form-label">Founder Prompts</label>
                      {[
                        {key:"founder_prompt1",prompt:"This project started because..."},
                        {key:"founder_prompt2",prompt:"The problem I'm obsessed with solving is..."},
                        {key:"founder_prompt3",prompt:"The kind of dev I vibe best with..."},
                        {key:"founder_prompt4",prompt:"If this works, in 5 years we'll..."},
                      ].map(({key,prompt})=>(
                        <div key={key} style={{background:"var(--cream)",border:"1.5px solid var(--border)",borderRadius:"14px",padding:"1rem",marginBottom:"0.75rem"}}>
                          <div style={{fontSize:"0.8rem",fontWeight:700,color:"var(--black)",marginBottom:"0.5rem"}}>{prompt}</div>
                          <textarea className="form-textarea" style={{minHeight:"60px",background:"white",border:"1px solid var(--border)"}} value={projForm[key]||""} onChange={e=>setProjForm(f=>({...f,[key]:e.target.value}))}/>
                        </div>
                      ))}
                    </div>
                    <div className="form-group full">
                      <label className="form-label">Project Images</label>
                      <div style={{fontSize:"0.75rem",color:"var(--muted)",marginBottom:"0.6rem"}}>Mockups, wireframes, screenshots -- up to 4</div>
                      <PortfolioImageUpload userId={user.id} images={projForm.project_images||[]} onUpdate={imgs=>setProjForm(f=>({...f,project_images:imgs}))}/>
                    </div>
                  </div>
                  <button className="form-submit" style={{marginTop:"1.5rem"}} onClick={saveProject} disabled={saving}>{saving?"Saving...":"Save changes"}</button>
                </div>
              ) : (
                <div key={p.id} className="dash-card" style={{marginBottom:"1rem"}}>
                  <div className="dash-card-title">{p.project_name}</div>
                  <div className="dash-card-meta">{p.industry} · {p.city} · {p.paid_type}</div>
                  <div style={{fontSize:"0.85rem",color:"var(--mid)",lineHeight:1.6,margin:"0.5rem 0 0.75rem"}}>{p.description}</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:"0.4rem",marginBottom:"0.75rem"}}>{(p.skills_needed||[]).map(s=><span key={s} className="skill-tag">{s}</span>)}</div>
                  <div className="dash-card-actions">
                    <button className="dash-btn dash-btn-primary" onClick={()=>openProjEdit(p)}>Edit project</button>
                    <button className="dash-btn" onClick={()=>onNavigate("browse-devs")}>Browse devs</button>
                  </div>
                </div>
              )
            ))}
            {myProjects.length === 0 && (
              <div className="empty-dash">
                <div className="empty-dash-title">No projects yet</div>
                <div className="empty-dash-sub">Submit your first project to start getting matched.</div>
                <button className="btn-primary" style={{marginTop:"1rem"}} onClick={()=>onNavigate("submit-project")}>Submit a project →</button>
              </div>
            )}
            {!editingProject && myProjects.length > 0 && (
              <button className="dash-btn dash-btn-primary" style={{width:"100%",padding:"0.85rem",justifyContent:"center",display:"flex",gap:"0.5rem",alignItems:"center",marginTop:"0.5rem"}} onClick={()=>onNavigate("submit-project")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add another project
              </button>
            )}
          </div>
        )
      ) : (
        interests.length > 0 ? interests.map(i=>(
          <div key={i.id} className="interest-item">
            <div className="interest-avatar">{i.from_user_id?.slice(0,2).toUpperCase()}</div>
            <div>
              <div className="interest-name">Someone expressed interest</div>
              <div className="interest-meta">{new Date(i.created_at).toLocaleDateString("en-AU")}</div>
              {i.message && <div className="interest-msg">"{i.message}"</div>}
            </div>
          </div>
        )) : (
          <div className="empty-dash">
            <div className="empty-dash-title">No expressions of interest yet</div>
            <div className="empty-dash-sub">{isDev?"When someone connects with you, they'll appear here.":"When developers apply to your project, they'll appear here."}</div>
          </div>
        )
      )}
    </div>
  );
}



// ── SKELETON LOADER ───────────────────────────────────────────────────────────
function SkeletonDevCard() {
  return (
    <div className="skeleton-card">
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:"1rem"}}>
        <div className="skeleton skeleton-avatar"/>
        <div className="skeleton" style={{width:60,height:22,borderRadius:"100px"}}/>
      </div>
      <div className="skeleton skeleton-line med"/>
      <div className="skeleton skeleton-line short" style={{marginBottom:"1rem"}}/>
      <div className="skeleton skeleton-line long"/>
      <div className="skeleton skeleton-line long"/>
      <div className="skeleton skeleton-line med" style={{marginBottom:"1rem"}}/>
      <div style={{display:"flex",gap:"0.4rem"}}>
        {[60,80,55].map((w,i)=><div key={i} className="skeleton" style={{width:w,height:26,borderRadius:"100px"}}/>)}
      </div>
    </div>
  );
}

// ── TERMS & PRIVACY ───────────────────────────────────────────────────────────
function TermsPage() {
  return (
    <div className="legal">
      <h1>Terms of Service</h1>
      <div className="legal-date">Last updated: March 2026</div>
      <h2>1. About The Match Deck</h2>
      <p>The Match Deck is a platform that connects project founders, startups and businesses with developers and designers across Australia. By using this platform you agree to these terms.</p>
      <h2>2. Your Account</h2>
      <p>You are responsible for maintaining the security of your account. You must provide accurate information when creating your profile. You must be 18 or older to use this platform.</p>
      <h2>3. Profiles & Projects</h2>
      <p>By submitting a developer profile or project, you confirm that the information provided is accurate and your own. The Match Deck reserves the right to remove any profile or project that violates these terms or is found to be misleading.</p>
      <h2>4. Connections & Communication</h2>
      <p>The Match Deck facilitates introductions between parties. We are not responsible for the outcome of any project engagement, payment dispute, or professional relationship formed through the platform.</p>
      <h2>5. Intellectual Property</h2>
      <p>You retain ownership of any content you submit. By submitting, you grant The Match Deck a licence to display your profile information to other users of the platform.</p>
      <h2>6. Prohibited Use</h2>
      <ul>
        <li>Creating fake or misleading profiles</li>
        <li>Harassing or spamming other users</li>
        <li>Using the platform for any unlawful purpose</li>
        <li>Scraping or copying platform data without permission</li>
      </ul>
      <h2>7. Limitation of Liability</h2>
      <p>The Match Deck is provided "as is". We are not liable for any damages arising from your use of the platform, including any project engagements formed through it.</p>
      <h2>8. Contact</h2>
      <p>Questions? Email <a href="mailto:hello@thematchdeck.com" style={{color:"var(--rose)"}}>hello@thematchdeck.com</a></p>
    </div>
  );
}

function PrivacyPage() {
  return (
    <div className="legal">
      <h1>Privacy Policy</h1>
      <div className="legal-date">Last updated: March 2026</div>
      <h2>1. What we collect</h2>
      <p>When you create an account or submit a profile we collect your name, email address, and any profile information you choose to provide such as skills, bio, portfolio links and profile photo.</p>
      <h2>2. How we use your data</h2>
      <ul>
        <li>To display your profile to other users of the platform</li>
        <li>To send you notifications about connections and interest in your profile or projects</li>
        <li>To improve the platform and matching experience</li>
      </ul>
      <h2>3. Who we share data with</h2>
      <p>We do not sell your data. Your profile information is visible to other registered users of The Match Deck. We use Supabase for database hosting and Resend for email delivery.</p>
      <h2>4. Data storage</h2>
      <p>Your data is stored securely on Supabase servers. We are based in Australia and comply with the Australian Privacy Act 1988.</p>
      <h2>5. Your rights</h2>
      <p>You can request to view, update or delete your data at any time by emailing <a href="mailto:hello@thematchdeck.com" style={{color:"var(--rose)"}}>hello@thematchdeck.com</a>. We will respond within 30 days.</p>
      <h2>6. Cookies</h2>
      <p>We use minimal cookies required for authentication only. We do not use advertising or tracking cookies.</p>
      <h2>7. Contact</h2>
      <p>Privacy enquiries: <a href="mailto:hello@thematchdeck.com" style={{color:"var(--rose)"}}>hello@thematchdeck.com</a></p>
    </div>
  );
}

// ── CONTACT PAGE ──────────────────────────────────────────────────────────────
function ContactPage() {
  const [form, setForm] = useState({name:"",email:"",message:""});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    if (!form.name||!form.email||!form.message) return;
    setLoading(true);
    await sendEmail({ to:"hello@thematchdeck.com", subject:`Contact: ${form.name}`, html:`<p><strong>${form.name}</strong> (${form.email})</p><p>${form.message}</p>` });
    setSent(true); setLoading(false);
  };

  return (
    <div className="contact-wrap">
      <div className="section-label">Get in touch</div>
      <div className="contact-title">We'd love to hear from you.</div>
      <div className="contact-sub">Question, feedback, or just want to say hi -- reach out anytime.</div>
      {sent ? (
        <div className="success-state">
          <div className="success-icon">✦</div>
          <div className="success-title">Message sent!</div>
          <div className="success-sub">We'll get back to you at {form.email} shortly.</div>
        </div>
      ) : (
        <div className="form-card" style={{textAlign:"left"}}>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Name</label><input className="form-input" placeholder="Your name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" placeholder="your@email.com" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}/></div>
            <div className="form-group full"><label className="form-label">Message</label><textarea className="form-textarea" style={{minHeight:"120px"}} placeholder="What's on your mind?" value={form.message} onChange={e=>setForm(f=>({...f,message:e.target.value}))}/></div>
          </div>
          <button className="form-submit" onClick={handleSend} disabled={loading||!form.name||!form.email||!form.message}>{loading?"Sending...":"Send message"}</button>
        </div>
      )}
      <a href="mailto:hello@thematchdeck.com" className="contact-card" style={{marginTop:"1.5rem",display:"block",textDecoration:"none"}}>
        <div className="contact-card-title">hello@thematchdeck.com</div>
        <div className="contact-card-sub">We aim to respond within 1 business day</div>
      </a>
    </div>
  );
}

// ── PASSWORD RESET ────────────────────────────────────────────────────────────
function PasswordResetPage({ onDone }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleReset = async () => {
    if (!email) return;
    setLoading(true); setError(null);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, { redirectTo:"https://thematchdeck.netlify.app" });
    if (err) { setError(err.message); setLoading(false); return; }
    setSent(true); setLoading(false);
  };

  return (
    <div className="reset-wrap">
      <div className="auth-box" style={{maxWidth:400}}>
        <div className="auth-logo">The Match<span>Deck</span></div>
        <div className="auth-title">{sent?"Check your inbox":"Reset your password"}</div>
        <div className="auth-sub">{sent?`We've sent a reset link to ${email}.`:"Enter your email and we'll send you a link to reset your password."}</div>
        {!sent && (
          <>
            {error && <div className="auth-error">{error}</div>}
            <input className="auth-input" type="email" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleReset()}/>
            <button className="auth-btn" onClick={handleReset} disabled={loading||!email}>{loading?"Sending...":"Send reset link"}</button>
          </>
        )}
        <div className="auth-switch"><button onClick={onDone}>← Back to log in</button></div>
      </div>
    </div>
  );
}

// ── ADMIN PAGE ────────────────────────────────────────────────────────────────
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "M@tchd3ck2026";

function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState(false);
  const [tab, setTab] = useState("devs");
  const [devs, setDevs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleLogin = () => { if (pw === ADMIN_PASSWORD) setAuthed(true); else setPwError(true); };

  useEffect(() => {
    if (!authed) return;
    Promise.all([
      supabase.from("developers").select("*").order("created_at",{ascending:false}),
      supabase.from("projects").select("*").order("created_at",{ascending:false}),
      supabase.from("profiles").select("*").order("created_at",{ascending:false}),
      supabase.from("interests").select("*").order("created_at",{ascending:false}),
    ]).then(([{data:d},{data:p},{data:u},{data:i}]) => {
      setDevs(d||[]); setProjects(p||[]); setUsers(u||[]); setInterests(i||[]);
      setLoading(false);
    });
  }, [authed]);

  const handleDelete = async (table, id) => {
    if (!window.confirm("Delete this permanently?")) return;
    await supabase.from(table).delete().eq("id", id);
    if (table==="developers") setDevs(d=>d.filter(x=>x.id!==id));
    if (table==="projects") setProjects(p=>p.filter(x=>x.id!==id));
    if (table==="profiles") setUsers(u=>u.filter(x=>x.id!==id));
  };

  const handleToggle = async (table, id, current) => {
    await supabase.from(table).update({approved:!current}).eq("id",id);
    if (table==="developers") setDevs(d=>d.map(x=>x.id===id?{...x,approved:!current}:x));
    if (table==="projects") setProjects(p=>p.map(x=>x.id===id?{...x,approved:!current}:x));
  };

  if (!authed) return (
    <div className="admin-gate">
      <div className="admin-gate-box">
        <div className="admin-gate-logo">The Match<span>Deck</span></div>
        <div className="admin-gate-title">Admin access only</div>
        <input className="auth-input" type="password" placeholder="Password" value={pw}
          onChange={e=>{setPw(e.target.value);setPwError(false);}} onKeyDown={e=>e.key==="Enter"&&handleLogin()}
          style={{background:"#1e1e1e",border:"1.5px solid #2a2a2a",color:"white"}}/>
        {pwError&&<div style={{color:"var(--rose)",fontSize:"0.82rem",marginBottom:"0.75rem"}}>Incorrect password.</div>}
        <button className="auth-btn" onClick={handleLogin}>Enter</button>
      </div>
    </div>
  );

  return (
    <div className="admin">
      <div className="admin-header">
        <div className="admin-title">Admin Dashboard</div>
        <div style={{fontSize:"0.82rem",color:"var(--muted)"}}>The Match Deck</div>
      </div>
      <div className="admin-stats">
        <div className="admin-stat"><div className="admin-stat-num">{devs.length}</div><div className="admin-stat-label">Developers</div></div>
        <div className="admin-stat"><div className="admin-stat-num">{projects.length}</div><div className="admin-stat-label">Projects</div></div>
        <div className="admin-stat"><div className="admin-stat-num">{users.length}</div><div className="admin-stat-label">Users</div></div>
        <div className="admin-stat"><div className="admin-stat-num">{interests.length}</div><div className="admin-stat-label">Connections</div></div>
      </div>
      <div className="admin-tabs">
        {["devs","projects","users","interests"].map(t=>(
          <button key={t} className={`admin-tab ${tab===t?"active":""}`} onClick={()=>setTab(t)}>{t.charAt(0).toUpperCase()+t.slice(1)}</button>
        ))}
      </div>
      {loading ? <div style={{color:"var(--muted)",padding:"2rem"}}>Loading...</div> : (
        <>
          {tab==="devs" && <table className="admin-table"><thead><tr><th>Name</th><th>Email</th><th>City</th><th>Level</th><th>Status</th><th>Date</th><th>Actions</th></tr></thead><tbody>
            {devs.map(d=><tr key={d.id}><td><strong>{d.name}</strong></td><td>{d.email}</td><td>{d.city}</td><td>{d.level}</td>
              <td><span className={`admin-badge ${d.approved?"admin-badge-dev":"admin-badge-owner"}`}>{d.approved?"Live":"Hidden"}</span></td>
              <td>{new Date(d.created_at).toLocaleDateString("en-AU")}</td>
              <td><button className="admin-action" onClick={()=>handleToggle("developers",d.id,d.approved)}>{d.approved?"Hide":"Approve"}</button><button className="admin-action danger" onClick={()=>handleDelete("developers",d.id)}>Delete</button></td>
            </tr>)}
          </tbody></table>}
          {tab==="projects" && <table className="admin-table"><thead><tr><th>Project</th><th>Founder</th><th>Email</th><th>Industry</th><th>Status</th><th>Date</th><th>Actions</th></tr></thead><tbody>
            {projects.map(p=><tr key={p.id}><td><strong>{p.project_name}</strong></td><td>{p.founder_name}</td><td>{p.founder_email}</td><td>{p.industry}</td>
              <td><span className={`admin-badge ${p.approved?"admin-badge-dev":"admin-badge-owner"}`}>{p.approved?"Live":"Hidden"}</span></td>
              <td>{new Date(p.created_at).toLocaleDateString("en-AU")}</td>
              <td><button className="admin-action" onClick={()=>handleToggle("projects",p.id,p.approved)}>{p.approved?"Hide":"Approve"}</button><button className="admin-action danger" onClick={()=>handleDelete("projects",p.id)}>Delete</button></td>
            </tr>)}
          </tbody></table>}
          {tab==="users" && <table className="admin-table"><thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Joined</th><th>Actions</th></tr></thead><tbody>
            {users.map(u=><tr key={u.id}><td><strong>{u.name||"--"}</strong></td><td>{u.email}</td>
              <td><span className={`admin-badge ${u.role==="developer"?"admin-badge-dev":"admin-badge-owner"}`}>{u.role}</span></td>
              <td>{new Date(u.created_at).toLocaleDateString("en-AU")}</td>
              <td><button className="admin-action danger" onClick={()=>handleDelete("profiles",u.id)}>Delete</button></td>
            </tr>)}
          </tbody></table>}
          {tab==="interests" && <table className="admin-table"><thead><tr><th>From</th><th>Message</th><th>Date</th></tr></thead><tbody>
            {interests.map(i=><tr key={i.id}><td>{i.from_user_id?.slice(0,8)}...</td><td>{i.message||"--"}</td><td>{new Date(i.created_at).toLocaleDateString("en-AU")}</td></tr>)}
          </tbody></table>}
        </>
      )}
    </div>
  );
}

export default function App() {
  const [view, setView] = useState(window.location.search.includes("admin") ? "admin" : "home");
  const [saved, setSaved] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authRole, setAuthRole] = useState(null);
  const [interestTarget, setInterestTarget] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) { setUser(session.user); loadProfile(session.user.id); }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) { setUser(session.user); loadProfile(session.user.id); }
      else { setUser(null); setProfile(null); }
    });
    return () => subscription.unsubscribe();
  }, []);

  // SEO meta tags
  useEffect(() => {
    const metas = {
      description: "The Match Deck connects project founders and startups with talented developers and designers across Australia.",
      "og:title": "The Match Deck -- Match your idea with the right developer",
      "og:description": "Australia's platform for connecting project founders with developers. Browse profiles, submit projects, and get matched.",
      "og:url": "https://thematchdeck.netlify.app",
      "og:type": "website",
      "twitter:card": "summary_large_image",
    };
    document.title = "The Match Deck -- Match your idea with the right developer";
    Object.entries(metas).forEach(([name, content]) => {
      let el = document.querySelector(`meta[name="${name}"],meta[property="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(name.startsWith("og:") ? "property" : "name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    });
  }, []);

  const loadProfile = async (userId) => {
    const { data } = await supabase.from("profiles").select("*").eq("id", userId).single();
    setProfile(data);
  };

  const handleAuthSuccess = ({ user: u, role, name }) => {
    setUser(u); setProfile({ role, name });
    setShowAuth(false); setView("dashboard");
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null); setProfile(null); setView("home");
  };

  const handleSave = dev => setSaved(s => s.some(x=>x.id===dev.id) ? s.filter(x=>x.id!==dev.id) : [...s,dev]);
  const showSearch = ["browse-devs","browse-projects"].includes(view);
  const handleNav = (id) => { setView(id); setSearchQuery(""); window.scrollTo({top:0,behavior:"smooth"}); };

  // Admin -- full screen takeover
  if (view === "admin") return (
    <>
      <style>{styles}</style>
      <AdminPage />
      <button onClick={()=>handleNav("home")} style={{position:"fixed",bottom:"1.5rem",right:"1.5rem",background:"var(--black)",color:"white",border:"none",padding:"0.6rem 1.2rem",borderRadius:"100px",cursor:"pointer",fontSize:"0.8rem",fontFamily:"'DM Sans',sans-serif",fontWeight:600}}>← Back to site</button>
    </>
  );

  // Password reset -- standalone page
  if (view === "reset-password") return (
    <>
      <style>{styles}</style>
      <PasswordResetPage onDone={()=>{ setView("home"); setShowAuth(true); }} />
    </>
  );

  // Connect button -- prompt login if not authed
  const handleConnect = (target, type) => {
    if (!user) { setAuthRole(type === "dev" ? "owner" : "developer"); setShowAuth(true); return; }
    setInterestTarget(target);
  };

  // Mobile nav adapts when logged in
  const mobileItems = user ? [
    { id:"home", label:"Home", svg:<svg viewBox="0 0 24 24"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H5a1 1 0 01-1-1V9.5z"/><path d="M9 21V12h6v9"/></svg> },
    { id:"browse-devs", label:"Devs", svg:<svg viewBox="0 0 24 24"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/><path d="M16 3.13a4 4 0 010 7.75"/><path d="M21 21v-2a4 4 0 00-3-3.87"/></svg> },
    { id:"browse-projects", label:"Projects", svg:<svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M17.5 14v6M14 17.5h6"/></svg> },
    { id: profile?.role==="developer" ? "dev-signup" : "submit-project", label: profile?.role==="developer" ? "Profile" : "Submit", svg:<svg viewBox="0 0 24 24"><path d="M12 5v14M5 12l7-7 7 7"/></svg> },
    { id:"dashboard", label:"Me", svg:<svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
  ] : [
    { id:"home", label:"Home", svg:<svg viewBox="0 0 24 24"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H5a1 1 0 01-1-1V9.5z"/><path d="M9 21V12h6v9"/></svg> },
    { id:"browse-devs", label:"Devs", svg:<svg viewBox="0 0 24 24"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/><path d="M16 3.13a4 4 0 010 7.75"/><path d="M21 21v-2a4 4 0 00-3-3.87"/></svg> },
    { id:"browse-projects", label:"Projects", svg:<svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M17.5 14v6M14 17.5h6"/></svg> },
    { id:"submit-project", label:"Submit", svg:<svg viewBox="0 0 24 24"><path d="M12 5v14M5 12l7-7 7 7"/></svg> },
    { id:"dev-signup", label:"Join", svg:<svg viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg> },
  ];

  const navItems = [
    { id:"home", label:"Home" },
    { id:"browse-devs", label:"Browse Devs" },
    { id:"browse-projects", label:"Projects" },
    { id:"dev-signup", label:"Join as Dev" },
    { id:"submit-project", label:"Submit Project" },
  ];

  // Admin -- full screen takeover
  if (view === "admin") return (
    <>
      <style>{styles}</style>
      <AdminPage />
      <button onClick={()=>handleNav("home")} style={{position:"fixed",bottom:"1.5rem",right:"1.5rem",background:"var(--black)",color:"white",border:"none",padding:"0.6rem 1.2rem",borderRadius:"100px",cursor:"pointer",fontSize:"0.8rem",fontFamily:"'DM Sans',sans-serif",fontWeight:600}}>← Back to site</button>
    </>
  );

  // Password reset -- standalone page
  if (view === "reset-password") return (
    <>
      <style>{styles}</style>
      <PasswordResetPage onDone={()=>{ setView("home"); setShowAuth(true); }} />
    </>
  );

  return (
    <>
      <style>{styles}</style>
      <div className="noise" />
      <div className="app">

        <nav className="nav">
          <div className="nav-logo" onClick={()=>handleNav("home")}>The Match<span>Deck</span></div>
          <div className="nav-tabs">
            {navItems.map(t=><button key={t.id} className={`nav-tab ${view===t.id?"active":""}`} onClick={()=>handleNav(t.id)}>{t.label}</button>)}
          </div>
          <div className="nav-right">
            <button className="saved-btn" onClick={()=>setShowSaved(true)}>
              ♥ Saved {saved.length > 0 && <span className="saved-count">{saved.length}</span>}
            </button>
            {user ? (
              <button className="nav-cta" onClick={()=>handleNav("dashboard")}>
                {profile?.name ? profile.name.split(" ")[0] : "Dashboard"} →
              </button>
            ) : (
              <button className="nav-cta" onClick={()=>setShowAuth(true)}>Log in →</button>
            )}
          </div>
        </nav>

        {showSearch && (
          <div className="search-bar-wrap">
            <div className="search-bar">
              <span className="search-icon"><svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
              <input className="search-input" placeholder={view==="browse-devs"?"Search by name, skill, city...":"Search projects by name, skill, city..."} value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}/>
              {searchQuery && <button className="search-clear" onClick={()=>setSearchQuery("")}>✕</button>}
            </div>
          </div>
        )}

        {view==="home" && <><Hero onNavigate={handleNav}/><HowItWorks/><AIMatch/></>}
        {view==="browse-devs" && <BrowseDevs saved={saved} onSave={handleSave} searchQuery={searchQuery} user={user} onConnect={(dev)=>handleConnect({dev:dev},"dev")} onNavigate={handleNav}/>}
        {view==="browse-projects" && <BrowseProjects searchQuery={searchQuery} user={user} onConnect={(proj)=>handleConnect({project:proj},"project")} onNavigate={handleNav}/>}
        {view==="dev-signup" && <DevSignup user={user} onNeedAuth={()=>{setAuthRole("developer");setShowAuth(true);}}/>}
        {view==="submit-project" && <SubmitProject user={user} onNeedAuth={()=>{setAuthRole("owner");setShowAuth(true);}}/>}
        {view==="dashboard" && user && <Dashboard user={user} profile={profile} onNavigate={handleNav} onLogout={handleLogout}/>}
        {view==="terms" && <TermsPage/>}
        {view==="privacy" && <PrivacyPage/>}
        {view==="contact" && <ContactPage/>}

        <div className="footer">
          <div className="footer-logo">The Match<span>Deck</span></div>
          <div style={{display:"flex",gap:"1.5rem",alignItems:"center",flexWrap:"wrap",justifyContent:"center"}}>
            <button onClick={()=>handleNav("terms")} style={{background:"none",border:"none",color:"var(--muted)",fontSize:"0.75rem",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Terms of Service</button>
            <button onClick={()=>handleNav("privacy")} style={{background:"none",border:"none",color:"var(--muted)",fontSize:"0.75rem",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Privacy Policy</button>
            <button onClick={()=>handleNav("contact")} style={{background:"none",border:"none",color:"var(--muted)",fontSize:"0.75rem",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Contact</button>
          </div>
          <div className="footer-text">Built for the Australian women's tech community</div>
        </div>

        <nav className="mobile-nav">
          <div className="mobile-nav-inner">
            {mobileItems.map(item=>(
              <button key={item.id} className={`mobile-nav-btn ${view===item.id?"active":""}`} onClick={()=>handleNav(item.id)}>
                <span className="mobile-nav-icon">{item.svg}</span>
                <span className="mobile-nav-label">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>

        {showSaved && <SavedPanel saved={saved} onUnsave={handleSave} onClose={()=>setShowSaved(false)}/>}
        {showAuth && <AuthModal defaultRole={authRole} onClose={()=>setShowAuth(false)} onSuccess={handleAuthSuccess} onForgotPassword={()=>{setShowAuth(false);setView("reset-password");}}/>}
        {interestTarget && <InterestModal dev={interestTarget.dev} project={interestTarget.project} user={user} onClose={()=>setInterestTarget(null)}/>}
      </div>
    </>
  );
}

